/**
 * ui.js — the studio: preview, seam editing, transport, panels, export.
 *
 * Structure of the screen, and why:
 *
 *   stage      one canvas rendered by scene.js + one CHROME canvas above it.
 *              Handles, the seam line and the difference map are drawn on the
 *              chrome layer, never inside the renderer — the renderer's output
 *              has to be exactly the exported file, and a selection handle
 *              baked into the frame is the fastest way to lose that guarantee.
 *   transport  play / scrub. Audio is the FINISHED MIX played as one buffer,
 *              not the elements' own tracks, so what you hear while editing is
 *              what the file will contain, ducking and offsets included.
 *   timeline   see timeline.js.
 *   panels     Frame · Seam · Voices · Music · Export.
 *
 * Preview and export share the renderer and differ only by a scale factor; the
 * chrome layer is the single exception and it never touches the frame canvas.
 *
 * Exposes window.CloneUI.
 */
(function () {
'use strict';
if (window.CloneUI) return;

var SLUG = 'clone-effect';
var T = {};
var P = null;                 // project
var prev = null;              // PreviewSources
var tl = null;                // Timeline
var els = {};                 // dom cache
var sel = 1;                  // layer being edited (never 0 — 0 is the scene)
var panel = 'seam';
var editMode = 'seam';        // 'seam' | 'layer'
var playing = false;
var playhead = 0;
var showDiff = false;
var diffCache = null;
var mixState = { buffer: null, dirty: true, building: false };
var actx = null, mixNode = null, mixStartedAt = 0, mixStartOffset = 0;
var rafId = 0;
/* Keyed by clip ID, never by index. Removing a take splices the array and every
   later index shifts by one — an index-keyed matte then belongs to a DIFFERENT
   take than the one it was built from, and the tool applies one performer's
   silhouette to another. Nothing errors; the picture is just wrong. */
var mattes = {};              // clipId → matte canvas (preview)
var matteEngines = {};        // clipId → PlateMatte | AiMatte
var lastMatteAt = 0;
var exportAbort = null;
var aspectAdopted = false;
var soloIndex = null;
var turnGap = 0.35;
var history = null;
var clipStore = {};      // clip id → the live clip object, INCLUDING removed ones still reachable through undo
var pendingDraft = null; // a draft whose files the user has yet to re-pick
var restoring = false;   // seconds of air between one take's last word and the next one's first

/* ─────────────────────────────── helpers ─────────────────────────────── */

function $(id) { return document.getElementById(id); }
function t(k, fb) { return (T && T[k]) || fb || k; }
function ae(type, data) {
    try {
        data = data || {};
        if (!data.slug) data.slug = SLUG;
        window.__ae && window.__ae(type, data);
    } catch (_) {}
}
function stage(name) { try { window.__stage && window.__stage(name); } catch (_) {} }
function toast(msg, kind) { try { window.__toast && window.__toast(msg, kind || 'info'); } catch (_) {} }

function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
}

function row(labelText, control, hint) {
    var r = el('div', 'ce-row');
    var l = el('span', 'ce-row-label', labelText);
    r.appendChild(l);
    r.appendChild(control);
    if (hint) {
        var h = el('div', 'ce-hint', hint);
        var wrap = el('div', 'ce-row-wrap');
        wrap.appendChild(r); wrap.appendChild(h);
        return wrap;
    }
    return r;
}

function slider(min, max, step, value, oninput) {
    var s = el('input', 'ce-slider');
    s.type = 'range'; s.min = min; s.max = max; s.step = step; s.value = value;
    s.addEventListener('input', function () { oninput(parseFloat(s.value), s); });
    return s;
}

function button(label, cls, onclick) {
    var b = el('button', 'ce-btn' + (cls ? ' ' + cls : ''), label);
    b.type = 'button';
    b.addEventListener('click', onclick);
    return b;
}

function chipStrip(items, current, onpick) {
    var strip = el('div', 'ce-chips');
    items.forEach(function (it) {
        var b = el('button', 'ce-chip' + (it.id === current ? ' active' : ''), it.label);
        b.type = 'button';
        b.dataset.id = it.id;
        if (it.title) b.title = it.title;
        b.addEventListener('click', function () {
            strip.querySelectorAll('.ce-chip').forEach(function (x) { x.classList.remove('active'); });
            b.classList.add('active');
            onpick(it.id);
        });
        strip.appendChild(b);
    });
    return strip;
}

function fmtTime(x) { return window.CloneTimeline.fmt(Math.max(0, x || 0)); }

/* ─────────────────────────────── init ─────────────────────────────── */

function init(translations) {
    T = translations || {};
    P = window.CloneScene.newProject();
    prev = new window.CloneSources.PreviewSources(P);

    els.intake = $('ceIntake');
    els.workspace = $('ceWorkspace');
    els.canvas = $('ceCanvas');
    els.overlay = $('ceOverlay');
    els.stageBox = $('ceStage');
    els.panels = $('cePanels');
    els.tabs = $('ceTabs');
    els.layers = $('ceLayers');
    els.transport = $('ceTransport');
    els.result = $('ceResult');

    els.ctx = els.canvas.getContext('2d');
    els.octx = els.overlay.getContext('2d');

    wireUploader();
    wireRecordButton();
    wireExportControls();
    buildTransport();
    buildTabs();
    buildTimeline();
    bindDemo();
    bindStage();
    bindKeys();
    restorePrefs();
    history = window.CloneDraft ? new window.CloneDraft.History() : null;
    wireHistoryButtons();
    offerDraft();

    window.addEventListener('resize', function () { layoutStage(); renderAll(); });
    stage('landed');
    announceCaps();
}

/* ──────────────────────── can this browser do the job? ────────────────────
   Asked at the DOOR, not at the Save button. Measured on WebKit: a browser
   with no WebCodecs and no MediaRecorder let the user upload two takes, place
   a seam, arrange the voices — and only then had nothing to export with. The
   work is the expensive part; finding out afterwards is the worst possible
   order.

   The snapshot also goes to telemetry once per session, because "which lane
   would this device have got" is otherwise only knowable for people who
   reached an export — i.e. exactly the ones who did NOT hit the problem. */
function probeCaps() {
    var c = {
        enc: typeof VideoEncoder !== 'undefined' ? 1 : 0,
        dec: typeof VideoDecoder !== 'undefined' ? 1 : 0,
        aenc: typeof AudioEncoder !== 'undefined' ? 1 : 0,
        rec: typeof MediaRecorder !== 'undefined' ? 1 : 0,
        // Проверка возможностей не имеет права ПРЕДПОЛАГАТЬ возможность.
        // Все соседние строки спрашивают через typeof, а эта разыменовывала
        // глобал: если audio.js не догрузился (мобильная сеть, блокировщик,
        // отказ и основного адреса, и запасного), probeCaps бросал, унося
        // init() целиком — тул умирал молча (Sentry G2, Chrome Mobile 151).
        // Ирония в том, что ветка «звука нет» ниже уже существует и показывает
        // человеку сообщение; до неё просто не доходило.
        audio: (window.CloneAudio && typeof window.CloneAudio.audioAvailable === 'function'
                && window.CloneAudio.audioAvailable()) ? 1 : 0,
        rvfc: ('requestVideoFrameCallback' in HTMLVideoElement.prototype) ? 1 : 0,
        touch: ('ontouchstart' in window) ? 1 : 0,
        dpr: Math.round((window.devicePixelRatio || 1) * 10) / 10
    };
    c.canExport = (c.enc || c.rec) ? 1 : 0;
    // Which lane this device WOULD take, named the same way the export does.
    c.lane = c.enc ? (c.aenc ? 'fast' : 'split') : (c.rec ? 'mr' : 'none');
    return c;
}

function announceCaps() {
    var c = probeCaps();
    ae('feature_use', { f: 'caps', enc: c.enc, dec: c.dec, aenc: c.aenc, rec: c.rec,
                        audio: c.audio, rvfc: c.rvfc, touch: c.touch, lane: c.lane });
    if (c.canExport && c.audio) return c;

    var host = els.intake;
    if (!host) return c;
    var box = el('div', 'ce-blocker');
    box.setAttribute('role', 'status');
    box.appendChild(el('strong', null, c.canExport ? t('cap_no_audio_title') : t('cap_no_export_title')));
    box.appendChild(el('div', 'ce-hint', c.canExport ? t('cap_no_audio_body') : t('cap_no_export_body')));
    host.insertBefore(box, host.firstChild);
    // A refusal the user can SEE is a different event from a crash, and it is
    // the one that predicts a bounce.
    try { window.__aeBlocked && window.__aeBlocked({ why: c.canExport ? 'no-audio' : 'no-export', lane: c.lane }); } catch (_) {}
    return c;
}

/* ─────────────────────── undo, and surviving a reload ───────────────────
   Snapshots rather than commands: measured at 0.008 ms and ~1 KB per step on an
   ordinary project (1.5 ms / 235 KB once the mask is hand-painted), so the cost
   of the simple design is nothing, and a command/inverse scheme would buy that
   nothing with a class of bugs where an inverse drifts from its operation.

   The snapshot stores clip IDENTITY, not clip objects, and the objects live in
   `clipStore`. That is what makes "undo the removal of a take" work at all: the
   File cannot be reconstructed from JSON, so removal keeps the object (and its
   object URL) alive as long as any snapshot still names it. */

function snapshot() {
    return JSON.stringify({
        W: P.W, H: P.H, fps: P.fps, preset: P.preset, quality: P.quality,
        frame: P.frame,
        order: P.clips.map(function (c) { return c.id; }),
        sel: sel,
        turnGap: turnGap,
        clips: P.clips.map(function (c) {
            return {
                id: c.id,
                trimIn: c.trimIn, trimOut: c.trimOut, start: c.start, hold: c.hold,
                opacity: c.opacity,
                dx: c.dx, dy: c.dy, scale: c.scale, fit: c.fit, flipH: c.flipH,
                exposure: c.exposure, tint: c.tint, offset: c.offset,
                volume: c.volume, muted: c.muted, audioOffset: c.audioOffset,
                visible: c.visible, mask: c.mask
            };
        }),
        music: P.music ? {
            volume: P.music.volume, fadeIn: P.music.fadeIn, fadeOut: P.music.fadeOut,
            loop: P.music.loop, duck: P.music.duck, duckDepth: P.music.duckDepth,
            start: P.music.start, trimIn: P.music.trimIn, trimOut: P.music.trimOut
        } : null
    });
}

/** Record a state worth coming back to, and keep the draft on disk current. */
function commit(reason) {
    if (restoring || !history) return;
    if (history.push(snapshot())) {
        ae('feature_use', { f: 'history_push', why: reason || '', depth: history.depth() });
    }
    updateHistoryButtons();
    if (window.CloneDraft && P.clips.length) window.CloneDraft.scheduleSave(P);
}

function applySnapshot(json) {
    var st;
    try { st = JSON.parse(json); } catch (_) { return; }
    restoring = true;

    P.W = st.W; P.H = st.H; P.fps = st.fps; P.preset = st.preset; P.quality = st.quality;
    if (st.frame) P.frame = { mode: st.frame.mode, win: st.frame.win ? st.frame.win.map(function (w) { return { x: w.x, y: w.y, w: w.w, h: w.h }; }) : null };
    turnGap = st.turnGap != null ? st.turnGap : turnGap;

    var byId = {};
    st.clips.forEach(function (c) { byId[c.id] = c; });

    // Rebuild the clip list from the recorded ORDER, pulling the live objects
    // back out of the store — this is what restores a removed take.
    var rebuilt = [];
    st.order.forEach(function (id) {
        var live = clipStore[id];
        if (!live) return;
        var saved = byId[id];
        if (saved) {
            for (var k in saved) {
                if (k === 'id') continue;
                if (Object.prototype.hasOwnProperty.call(saved, k)) live[k] = saved[k];
            }
        }
        rebuilt.push(live);
    });
    P.clips = rebuilt;

    if (P.music && st.music) {
        for (var mk in st.music) if (Object.prototype.hasOwnProperty.call(st.music, mk)) P.music[mk] = st.music[mk];
    }

    sel = Math.min(Math.max(st.sel || 1, 0), Math.max(0, P.clips.length - 1));
    if (P.clips.length >= 2 && sel === 0) sel = 1;

    // A take that was removed lost its <video>; the snapshot restores the object
    // and its settings, but nothing decodes for it, so the layer draws nothing
    // and undo looks like it half-worked. Measured on three takes: the middle
    // one came back in the model and stayed invisible on screen.
    var reattached = [];
    P.clips.forEach(function (c) {
        if (prev.has(c.id)) return;
        var v = document.createElement('video');
        v.muted = true; v.playsInline = true; v.preload = 'auto';
        v.src = c.url;
        // Same class the intake uses, so a restored element is not a slightly
        // different element — a second way of hiding a video is a second set of
        // rules to keep in step.
        v.className = 'ce-hidden-video';
        document.body.appendChild(v);
        prev.attach(c.id, v);
        reattached.push(c);
    });

    restoring = false;
    invalidateAudio();
    layoutStage();
    buildLayers(); buildPanels();
    tl && tl.render();
    updateTransport();
    refreshMattes(true);
    renderAll();

    if (reattached.length) {
        // readyState is not a promise that a frame can be drawn, so the restored
        // takes are primed the same way a freshly loaded one is, and the stage
        // is redrawn once they actually hold a picture.
        Promise.all(reattached.map(function (c) { return prev.prime(c.id, c.trimIn); }))
            .then(function () { refreshMattes(true); renderAll(); })
            .catch(function () {});
        ae('feature_use', { f: 'clip_reattached', n: reattached.length });
    }
    updateHistoryButtons();
    if (window.CloneDraft && P.clips.length) window.CloneDraft.scheduleSave(P);
}

function doUndo() {
    if (!history || !history.canUndo()) return;
    var j = history.undo();
    if (j) { applySnapshot(j); ae('feature_use', { f: 'undo', depth: history.depth() }); }
}

function doRedo() {
    if (!history || !history.canRedo()) return;
    var j = history.redo();
    if (j) { applySnapshot(j); ae('feature_use', { f: 'redo', depth: history.depth() }); }
}

/* Release takes that no snapshot can reach any more. Keeping every removed clip
   forever is the decoder-and-blob leak fixed earlier; destroying it on removal
   would make "undo the removal" impossible. Reachability from the history is the
   condition that satisfies both, and the byte cap guarantees it eventually
   becomes false. */
function gcClips() {
    if (!history) return;
    var live = {};
    P.clips.forEach(function (c) { live[c.id] = 1; });
    history.past.concat(history.future).forEach(function (json) {
        for (var id in clipStore) if (json.indexOf('"' + id + '"') !== -1) live[id] = 1;
    });
    for (var id in clipStore) {
        if (live[id]) continue;
        var c = clipStore[id];
        try { URL.revokeObjectURL(c.url); } catch (_) {}
        prev.detach(id);
        delete mattes[id]; delete matteEngines[id];
        delete clipStore[id];
        ae('feature_use', { f: 'clip_released' });
    }
}

function updateHistoryButtons() {
    var u = $('ceUndo'), r = $('ceRedo');
    if (u) u.disabled = !(history && history.canUndo());
    if (r) r.disabled = !(history && history.canRedo());
}

/* ── the draft: what a reload must not cost ──
   The bar is shown, not a modal: a modal in front of an empty tool demands a
   decision before the user has even seen what the draft is. */
async function offerDraft() {
    if (!window.CloneDraft) return;
    var d = null;
    try { d = await window.CloneDraft.load(); } catch (_) { return; }
    if (!d || !d.state || !d.state.clips || !d.state.clips.length) return;
    if (P.clips.length) return;                       // the user already started something

    var host = els.intake;
    if (!host) return;
    var bar = el('div', 'ce-draft-bar');
    bar.setAttribute('role', 'status');

    var mins = Math.max(1, Math.round(d.ageMs / 60000));
    var when = mins < 60 ? t('draft_ago_min').replace(':n', mins)
        : t('draft_ago_hour').replace(':n', Math.round(mins / 60));

    bar.appendChild(el('strong', null, t('draft_found').replace(':when', when)));
    bar.appendChild(el('div', 'ce-hint',
        d.withFiles ? t('draft_with_files') : t('draft_without_files')));

    var row = el('div', 'ce-chips');
    var go = button(t('draft_continue'), 'primary', async function () {
        bar.remove();
        ae('feature_use', { f: 'draft_restore', withFiles: d.withFiles ? 1 : 0, ageMin: mins });
        if (d.withFiles) {
            pendingDraft = d.state;
            var files = d.files.slice();
            if (d.music) files.__music = d.music;
            await addClips(files, { fromDraft: true, music: d.music });
        } else {
            pendingDraft = d.state;
            toast(t('draft_pick_same'), 'info');
        }
    });
    var fresh = button(t('draft_discard'), 'ghost', function () {
        bar.remove();
        window.CloneDraft.clear();
        ae('feature_use', { f: 'draft_discard', ageMin: mins });
    });
    row.appendChild(go); row.appendChild(fresh);
    bar.appendChild(row);
    host.insertBefore(bar, host.firstChild);
    ae('feature_use', { f: 'draft_offered', withFiles: d.withFiles ? 1 : 0, ageMin: mins });
}

/** Re-attach a clip's saved settings when the user picks the same file again. */
function applyDraftTo(clip) {
    if (!pendingDraft) return false;
    for (var i = 0; i < pendingDraft.clips.length; i++) {
        var sc = pendingDraft.clips[i];
        if (sc.__used) continue;
        if (!window.CloneDraft.sameFile(sc.file, clip.file)) continue;
        sc.__used = true;
        ['trimIn', 'trimOut', 'start', 'hold', 'dx', 'dy', 'scale', 'fit', 'flipH',
         'exposure', 'tint', 'offset', 'opacity', 'volume', 'muted', 'audioOffset', 'visible', 'mask']
            .forEach(function (k) { if (sc[k] !== undefined) clip[k] = sc[k]; });
        return true;
    }
    return false;
}

function wireHistoryButtons() {
    var u = $('ceUndo'), r = $('ceRedo');
    if (u) u.addEventListener('click', doUndo);
    if (r) r.addEventListener('click', doRedo);
    updateHistoryButtons();
}

/* ─────────────────────────── keyboard ───────────────────────────
   An editor without a spacebar is a surprise, and for a keyboard-only user it
   is a wall: every control here is reachable by Tab, but scrubbing a timeline
   one drag at a time is not usable. Arrow keys step a frame, Shift a second.

   The guard matters as much as the bindings: while focus is in a field or on a
   button, Space belongs to that control, not to the transport. */
function bindKeys() {
    document.addEventListener('keydown', function (e) {
        if (!P.clips.length) return;
        var el2 = e.target;
        var tag = (el2 && el2.tagName) || '';
        if (window.__keyOwnedByFocus ? window.__keyOwnedByFocus(e) : (/^(INPUT|TEXTAREA|SELECT|BUTTON)$/.test(tag) || (el2 && el2.isContentEditable))) return;
        var kl = window.__keyLetter ? window.__keyLetter(e) : (/^[a-z]$/i.test(e.key || '') ? e.key.toLowerCase() : '');
        if (e.ctrlKey || e.metaKey) {
            if (kl === 'z' && !e.shiftKey) { e.preventDefault(); doUndo(); return; }
            if ((kl === 'z' && e.shiftKey) || kl === 'y') { e.preventDefault(); doRedo(); return; }
            return;
        }
        if (e.altKey) return;

        var fps = P.fps || 30;
        var handled = true;
        switch (kl || e.key) {
            case ' ': case 'k': togglePlay(); break;
            case 'ArrowLeft':  seekTo(playhead - (e.shiftKey ? 1 : 1 / fps)); break;
            case 'ArrowRight': seekTo(playhead + (e.shiftKey ? 1 : 1 / fps)); break;
            case 'Home': seekTo(0); break;
            case 'End': seekTo(window.CloneScene.projectDuration(P)); break;
            default: handled = false;
        }
        if (handled) {
            e.preventDefault();
            ae('feature_use', { f: 'key', k: e.key === ' ' ? 'space' : (kl || e.key) });
        }
    });
}

/* ───────────────────── remembered between visits ─────────────────────
   Through the project's own primitive, not localStorage by hand. These are
   preferences a returning user should not have to set twice; the composition
   itself is deliberately NOT remembered — reopening the tool with someone
   else's half-finished edit would be worse than starting clean. */
function restorePrefs() {
    var TS = window.__toolSettings;
    if (!TS) return;
    try {
        P.quality = TS.load(SLUG, 'quality', P.quality);
        P.fps = parseInt(TS.load(SLUG, 'fps', P.fps), 10) || P.fps;
        var g = parseFloat(TS.load(SLUG, 'turnGap', turnGap));
        if (isFinite(g) && g >= -0.5 && g <= 2) turnGap = g;
        var hSel = $('ceHeight');
        if (hSel) {
            var h = TS.load(SLUG, 'height', null);
            // Only if the tier still allows it — a remembered 2160 must not
            // resurrect an option the gate has since disabled.
            if (h) {
                var opt = hSel.querySelector('option[value="' + h + '"]');
                if (opt && !opt.disabled) hSel.value = h;
            }
        }
        var fSel = $('ceFormat');
        if (fSel) {
            var f = TS.load(SLUG, 'format', null);
            if (f && fSel.querySelector('option[value="' + f + '"]')) fSel.value = f;
        }
    } catch (_) {}
}

function savePref(k, v) {
    try { window.__toolSettings && window.__toolSettings.save(SLUG, k, v); } catch (_) {}
}

/* ───────────────────────────── the demo ──────────────────────────────
   The page used to describe the effect in four sentences and never show it,
   which is exactly how somebody lands here and leaves without understanding
   what the tool makes. So the first thing on the page is the mechanic: two
   real takes from one tripod and the seam between them.

   One control drives it — a real <input type=range> laid over the frame with
   its own appearance. Pointer drag, arrow keys and assistive tech all move the
   same element, so there is no second path to keep in step with the first.
   The example itself is fetched only when somebody asks for it; the poster
   frames are 21 KB each and that is all the page pays to explain itself. */

function bindDemo() {
    var frame = $('ceDemoFrame'), range = $('ceDemoRange'), open = $('ceDemoOpen');
    if (!frame || !range) return;

    function paint() {
        var v = Math.max(0, Math.min(1, (+range.value || 0) / 1000));
        frame.style.setProperty('--seam', (v * 100).toFixed(2) + '%');
        frame.style.setProperty('--seamn', v.toFixed(3));
    }
    range.addEventListener('input', paint);
    paint();

    // A drag anywhere on the frame is the natural gesture here; it writes into
    // the same input rather than keeping a position of its own.
    var dragging = false;
    function fromEvent(e) {
        var r = frame.getBoundingClientRect();
        if (!r.width) return;
        range.value = String(Math.round(Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)) * 1000));
        paint();
    }
    frame.addEventListener('pointerdown', function (e) {
        if (e.button != null && e.button !== 0) return;
        dragging = true;
        try { frame.setPointerCapture(e.pointerId); } catch (_) {}
        fromEvent(e);
    });
    frame.addEventListener('pointermove', function (e) { if (dragging) fromEvent(e); });
    ['pointerup', 'pointercancel'].forEach(function (k) {
        frame.addEventListener(k, function (e) {
            if (!dragging) return;
            dragging = false;
            try { frame.releasePointerCapture(e.pointerId); } catch (_) {}
            ae('feature_use', { f: 'demo_dragged', pos: Math.round((+range.value || 0) / 10) });
        });
    });

    if (!open) return;
    open.addEventListener('click', async function () {
        if (open.disabled) return;
        open.disabled = true;
        var label = open.textContent;
        open.textContent = t('demo_loading');
        try {
            var files = [];
            for (var i = 0; i < 2; i++) {
                var name = i === 0 ? 'take-a' : 'take-b';
                var r = await fetch('/vendor/demos/clone-effect/' + name + '.mp4');
                if (!r.ok) throw new Error('HTTP ' + r.status);
                var blob = await r.blob();
                files.push(new File([blob], (i === 0 ? 'dubl-1' : 'dubl-2') + '.mp4',
                    { type: 'video/mp4', lastModified: Date.now() }));
            }
            ae('feature_use', { f: 'demo_opened' });
            await addClips(files);
        } catch (e) {
            open.disabled = false;
            open.textContent = label;
            toast(t('demo_failed'), 'warning');
            ae('feature_use', { f: 'demo_failed', err: ((e && e.message) || '').slice(0, 40) });
        }
    });
}

/* ─────────────────────────────── intake ─────────────────────────────── */

/* __bindUploaderFile, not a raw addEventListener: on a slow connection the
   per-tool scripts finish AFTER the user has already picked a file, the
   uploader has already dispatched its event, and a late listener receives
   nothing — the tool just sits there. The helper replays the pending pick. */
/* The intake gets the record button only when the browser can actually do it —
   offering a camera to a browser that has none is the same broken promise as
   offering an export to one that cannot encode. */
function wireRecordButton() {
    var host = $('ceRecordMount');
    if (!host) return;
    if (!window.CloneRecorder || !window.CloneRecorder.available()) return;
    host.hidden = false;
    host.appendChild(recordButton(t('rec_button')));
    host.appendChild(el('div', 'ce-hint', t('rec_hint')));
}

function wireUploader() {
    var drop = $('ceDrop');
    if (!drop || !window.__bindUploaderFile) return;
    window.__bindUploaderFile(drop, function (file, detail) {
        var files = (detail && detail.files && detail.files.length)
            ? Array.prototype.slice.call(detail.files)
            : (file ? [file] : []);
        addClips(files);
    });
}

/* ─────────────────────── shooting the takes here ───────────────────────
   The tool spends most of its cleverness repairing what went wrong before the
   user arrived: a bumped tripod, or both performers standing in the same half.
   Recording take 2 with take 1 showing through removes both at the source, so
   the button sits next to the uploader rather than in a submenu. */
function openRecorder() {
    if (!window.CloneRecorder || !window.CloneRecorder.available()) {
        toast(t('rec_unavailable'), 'warning');
        ae('feature_use', { f: 'record_unavailable' });
        return;
    }
    if (P.clips.length >= window.CloneScene.MAX_CLIPS) {
        toast(t('err_max_clips'), 'warning');
        return;
    }
    var base = P.clips[0] || null;
    window.CloneRecorder.open({
        t: t,
        ghostClip: base,
        takeNumber: P.clips.length + 1,
        width: (base && base.vw) || 1280,
        height: (base && base.vh) || 720,
        onFile: function (f) { addClips([f]); }
    });
}

function recordButton(label) {
    var b = button('\uD83D\uDCF9 ' + label, 'ghost ce-rec-open', openRecorder);
    return b;
}

async function addClips(files, opts) {
    var free = window.CloneScene.MAX_CLIPS - P.clips.length;
    if (free <= 0) { toast(t('err_max_clips'), 'warning'); return; }
    var take = files.slice(0, free);
    if (files.length > free) toast(t('err_max_clips'), 'warning');

    for (var i = 0; i < take.length; i++) {
        var f = take[i];
        try {
            var meta = await window.CloneSources.loadMeta(f);
            var c = window.CloneScene.newClip(f, meta.url, meta);
            c.mask = window.CloneMasks.make(P.clips.length === 0 ? 'none' : 'line');
            if (P.clips.length === 0) c.mask.type = 'none';
            if (opts && opts.fromDraft) { /* settings applied below, after priming */ }
            P.clips.push(c);
            clipStore[c.id] = c;
            var idx = P.clips.length - 1;
            prev.attach(c.id, meta.el);
            meta.el.className = 'ce-hidden-video';
            document.body.appendChild(meta.el);
            // Decode one frame before anything looks at this clip. Loading the
            // metadata is not enough: until the element has been seeked it draws
            // NOTHING, and every measurement downstream would then compare two
            // blank canvases and answer confidently about pixels that are not
            // there.
            // A restored take gets its saved edits BEFORE anything measures or
            // draws it — otherwise auto-composition would overwrite the seam the
            // user had already placed.
            var fromDraft = applyDraftTo(c);
            await prev.prime(c.id, c.trimIn);
            ae('feature_use', { f: 'clip_added', n: P.clips.length, dur: Math.round(meta.duration), draft: fromDraft ? 1 : 0 });
        } catch (err) {
            toast(t('err_load').replace(':name', f.name || ''), 'error');
            ae('feature_use', { f: 'clip_load_failed', err: ((err && err.message) || '').slice(0, 50) });
        }
    }

    if (P.clips.length >= 1) stage('file_picked');
    if (P.clips.length >= 1) {
        // The scene take decides the output shape. Keyed off "have we ever done
        // this", NOT off clips.length === 1: this runs AFTER the upload loop, so
        // dropping both takes at once — the normal way to use the tool — left
        // the count at 2 and skipped the adoption entirely. Portrait phone
        // footage then rendered into a 16:9 canvas.
        if (!aspectAdopted) { aspectAdopted = true; adoptAspect(P.clips[0]); }
        showWorkspace();
    }
    if (P.clips.length >= 2 && sel < 1) sel = 1;
    if (sel >= P.clips.length) sel = Math.max(1, P.clips.length - 1);

    invalidateAudio();
    computeSpans();
    buildPanels();
    buildLayers();
    layoutStage();
    tl && tl.render();
    updateTransport();
    renderAll();

    var restoredFlag = !!(pendingDraft && pendingDraft.clips.some(function (c) { return c.__used; }));

    // The framing is a PROJECT decision, not a per-clip one, so re-attaching the
    // takes does not bring it back on its own. A vertical composition that
    // reopens as a horizontal one looks like the work was lost.
    if (restoredFlag && pendingDraft.frame && pendingDraft.frame.mode) {
        P.frame = { mode: pendingDraft.frame.mode, win: pendingDraft.frame.win || null };
        layoutStage();
    }
    if (P.clips.length >= 2) {
        stage('composed');
        // Never re-derive a seam the user already has. Auto-composition is a
        // starting point, and a restored draft is past that point.
        var restored = restoredFlag;
        if (restored) {
            toast(t('draft_restored'), 'success');
        } else {
            setTimeout(function () { autoCompose(); }, 250);
        }
    }
    // A BASELINE, always. `canUndo` needs a state to go BACK to, so a history
    // whose first entry is the user's first edit can undo nothing — the button
    // sat disabled after the very change it was there to take back.
    if (P.clips.length) commit(restoredFlag ? 'draft_restored' : 'loaded');

    if (opts && opts.music && !P.music) {
        try {
            var octx = window.CloneAudio.offlineCtx(1, window.CloneAudio.SR);
            var mb = await window.CloneAudio.decode(opts.music, octx, null);
            P.music = window.CloneScene.newMusic(opts.music, { duration: mb ? mb.duration : 0 });
            if (pendingDraft && pendingDraft.music) {
                ['volume', 'fadeIn', 'fadeOut', 'loop', 'duck', 'duckDepth', 'start', 'trimIn', 'trimOut']
                    .forEach(function (k) { if (pendingDraft.music[k] !== undefined) P.music[k] = pendingDraft.music[k]; });
            }
            invalidateAudio(); buildPanels(); tl && tl.render();
        } catch (_) {}
    }
}

/* ─────────────────────────── opening on something real ───────────────────────
   The first thing the user sees decides whether this tool feels like an editor
   or like a puzzle. A fixed default seam is a coin flip: half the time it shows
   the empty half of the second take and the screen looks broken.

   So the opening state is MEASURED. Four sampled frames per take give a rough
   empty room; whatever differs from it is the performer, and their centroid says
   which part of the frame that take contributes. From two centroids follow all
   three unknowns at once: whether to split vertically or horizontally, where the
   seam goes, and which side each take keeps.

   Everything here fails soft — an unreadable take, a performer who never moved,
   a still frame — and falls back to the neutral vertical split. */
async function autoCompose() {
    if (P.clips.length < 2) return;
    if (P.clips.length > 2) { await autoComposeMany(); return; }
    setBusy(true, t('busy_compose'));
    try {
        // A knocked tripod comes first: every later measurement compares the two
        // takes pixel for pixel, so it has to run on aligned frames.
        runAlign(false);

        var cB = P.clips[1];
        var fa = prev.getFrame(0), fb = prev.getFrame(1);
        if (!fa || !fb) return;

        var seam = window.CloneAlign.analyseSeam(fa, fb);
        ae('feature_use', {
            f: 'auto_compose',
            ok: seam ? 1 : 0,
            angle: seam ? seam.angle : -1,
            pos: seam ? Math.round(seam.pos * 100) : -1,
            invert: seam && seam.invert ? 1 : 0,
            conf: seam ? Math.round(seam.confidence * 100) : 0,
            q: seam ? Math.round(seam.quality * 100) : -1
        });

        if (seam) {
            cB.mask.angle = seam.angle;
            cB.mask.pos = clamp01(seam.pos);
            cB.mask.invert = seam.invert;
            // A confident answer about WHERE is not an answer about WHETHER.
            // When the two performers overlap along the chosen axis, the best
            // straight seam still runs through one of them, and the composite
            // ships silently broken. Say it, and name the mask that does work.
            rememberAutoSeam(cB.mask);
            if (seam.quality < 0.55) {
                toast(t('compose_crowded'), 'warning');
                ae('feature_use', { f: 'compose_crowded', q: Math.round(seam.quality * 100) });
            }
        } else {
            // No confident reading — say so instead of committing to a side the
            // measurement did not support. A wrong side hides BOTH performers,
            // which reads as "the tool is broken", so silence is the worse
            // option here, not the safer one.
            toast(t('compose_unsure'), 'info');
        }

        // Auto-exposure metered the two takes differently often enough that
        // matching them belongs to opening the project, not to a repair step.
        var tone = window.CloneAlign.toneMatch(fa, fb);
        if (tone) {
            cB.exposure = tone.exposure;
            cB.tint = tone.tint;
            cB.offset = tone.offset;
        }

        buildPanels();
        renderAll();
        commit('auto_compose');
    } catch (e) {
        ae('feature_use', { f: 'auto_compose_failed', err: ((e && e.message) || '').slice(0, 50) });
    } finally {
        setBusy(false);
    }
}

/**
 * Composition for THREE or FOUR takes.
 *
 * Until now this ran only for exactly two, and every take past the second
 * arrived with the same default mask as the one before it — so they covered
 * each other and the composition showed one person where the user had shot
 * three. Nothing said so; it simply looked broken.
 *
 * A straight seam cannot separate three people, so each take gets a BAND, and
 * the band edges are placed between the performers rather than at even thirds:
 * an even split cuts whoever is not standing where the arithmetic expected.
 * Finding the performers is reframe.js's intersection trick — the region where
 * take i differs from EVERY other take is take i's own performer.
 */
async function autoComposeMany() {
    var R = window.CloneReframe;
    var n = P.clips.length;
    if (!R || n < 3) return;

    setBusy(true, t('busy_compose'));
    try {
        runAlign(false);

        var frames = [], i, j;
        for (i = 0; i < n; i++) {
            var f = prev.getFrame(i);
            if (!f) { toast(t('compose_unsure'), 'info'); return; }
            frames.push(f);
        }

        var fields = {}, fw = 0, fh = 0;
        for (i = 0; i < n; i++) {
            for (j = i + 1; j < n; j++) {
                var dm = window.CloneAlign.differenceMap(frames[i], frames[j]);
                if (!dm || !dm.field) continue;
                fields[i + '_' + j] = dm.field; fw = dm.w; fh = dm.h;
            }
        }
        var boxes = fw ? R.performerBoxes(fields, n, fw, fh) : [];
        var bands = R.bandsFor(boxes);
        ae('feature_use', {
            f: 'auto_compose_many', n: n,
            found: boxes.filter(Boolean).length, ok: bands ? 1 : 0
        });

        if (!bands) {
            // Better an even split the user can see and drag than a stack of
            // takes hiding each other with no explanation.
            bands = [];
            for (i = 0; i < n; i++) bands.push({ index: i, x0: i / n, x1: (i + 1) / n });
            toast(t('compose_many_even'), 'warning');
        } else {
            toast(t('compose_many_done').replace(':n', n), 'success');
        }

        // The first take is the base layer, drawn without a mask; every other
        // take gets its band painted on top. The base shows through wherever no
        // band covers, which includes its own — so the ORDER of the bands does
        // not matter, only that they do not overlap.
        for (i = 0; i < bands.length; i++) {
            var b = bands[i];
            var c = P.clips[b.index];
            if (!c) continue;
            if (b.index === 0) { c.mask.type = 'none'; continue; }
            c.mask.type = 'rect';
            c.mask.x = (b.x0 + b.x1) / 2;
            c.mask.w = Math.max(0.04, b.x1 - b.x0);
            c.mask.y = 0.5; c.mask.h = 1;
            c.mask.radius = 0;
            c.mask.invert = false;
        }

        // Auto-exposure metered each take differently; match them all to the
        // first, which is the one drawn without a mask.
        for (i = 1; i < n; i++) {
            var tone = window.CloneAlign.toneMatch(frames[0], frames[i]);
            if (!tone) continue;
            P.clips[i].exposure = tone.exposure;
            P.clips[i].tint = tone.tint;
            P.clips[i].offset = tone.offset;
        }

        buildPanels(); renderAll(); commit('auto_compose_many');
    } catch (e) {
        ae('feature_use', { f: 'auto_compose_many_failed', err: ((e && e.message) || '').slice(0, 50) });
    } finally { setBusy(false); }
}

function clamp01(v) { return Math.max(0.04, Math.min(0.96, v)); }

/* The one number that says whether the automatic seam is any good in the wild.
   A seam the user immediately drags is a seam the measurement got wrong, and
   without recording it the feature can only ever be judged on my own fixtures.
   Reported ONCE per project: the first correction is the verdict, the tenth
   nudge is just editing. */
var autoSeam = null, seamCorrectionSent = false;

function rememberAutoSeam(mask) {
    autoSeam = { pos: mask.pos, angle: mask.angle, invert: mask.invert, at: performance.now() };
    seamCorrectionSent = false;
}

function noteSeamCorrection(kind) {
    if (!autoSeam || seamCorrectionSent) return;
    var m = P.clips[sel] && P.clips[sel].mask;
    if (!m) return;
    var dPos = Math.abs((m.pos || 0) - autoSeam.pos);
    var dAng = Math.abs((m.angle || 0) - autoSeam.angle);
    var flipped = !!m.invert !== !!autoSeam.invert;
    // Ignore a twitch: only a move the user would notice counts as a correction.
    if (dPos < 0.04 && dAng < 5 && !flipped) return;
    seamCorrectionSent = true;
    ae('feature_use', {
        f: 'seam_corrected', kind: kind,
        dpos: Math.round(dPos * 100), dang: Math.round(dAng),
        flip: flipped ? 1 : 0,
        after_ms: Math.round(performance.now() - autoSeam.at)
    });
}

function adoptAspect(c) {
    if (!c.vw || !c.vh) return;
    var ar = c.vw / c.vh;
    var presets = [
        { id: '16:9', w: 1920, h: 1080 }, { id: '9:16', w: 1080, h: 1920 },
        { id: '1:1', w: 1080, h: 1080 }, { id: '4:5', w: 1080, h: 1350 }
    ];
    var best = presets[0], bestD = Infinity;
    presets.forEach(function (p) {
        var d = Math.abs(p.w / p.h - ar);
        if (d < bestD) { bestD = d; best = p; }
    });
    P.preset = best.id;
    applyOutputSize();
}

function showWorkspace() {
    els.workspace.classList.add('active');
    if (els.intake) els.intake.classList.add('ce-compact');
    var demo = $('ceDemo');
    if (demo) demo.classList.add('is-gone');
}

/* ───────────────────────────── layer chips ───────────────────────────── */

function buildLayers() {
    var host = els.layers;
    host.innerHTML = '';
    if (P.clips.length < 1) return;

    host.appendChild(el('span', 'ce-row-label', t('editing_layer')));

    // Take 1 is the SCENE and has no mask, so it was simply missing from this
    // row — a user with three takes saw "2, 3, 4" and no explanation of where
    // the first one went, and no way to replace it if they picked the wrong
    // file. It gets a chip of its own that says what its job is.
    for (var i = 0; i < P.clips.length; i++) {
        (function (i) {
            var label = i === 0 ? t('layer_scene') : t('layer_n').replace(':n', i + 1);
            var b = el('button', 'ce-chip' + (i === sel ? ' active' : '') + (i === 0 ? ' scene' : ''), label);
            b.type = 'button';
            b.title = i === 0 ? t('layer_scene_hint') : '';
            b.addEventListener('click', function () {
                sel = i; buildLayers(); buildPanels(); tl && tl.select(i); renderAll();
            });
            host.appendChild(b);
        })(i);
    }

    if (P.clips.length >= 2) {
        var solo = el('button', 'ce-chip' + (soloIndex != null ? ' active' : ''), '👁 ' + t('solo'));
        solo.type = 'button';
        solo.title = t('solo_hint');
        solo.addEventListener('click', function () {
            soloIndex = (soloIndex === sel) ? null : sel;
            buildLayers(); renderAll();
            ae('feature_use', { f: 'solo', on: soloIndex != null ? 1 : 0, i: sel });
        });
        host.appendChild(solo);
    }

    if (window.CloneRecorder && window.CloneRecorder.available()
        && P.clips.length < window.CloneScene.MAX_CLIPS) {
        var more = el('button', 'ce-chip', '\uD83D\uDCF9 ' + t('rec_button_more'));
        more.type = 'button';
        more.title = t('rec_button_more_hint');
        more.addEventListener('click', openRecorder);
        host.appendChild(more);
    }

    var rm = button(t('remove_layer'), 'ghost', function () { removeClip(sel); });
    rm.classList.add('ce-chip-action');
    host.appendChild(rm);
}

function removeClip(i) {
    // Any take can go, take 1 included: picking the wrong scene file used to
    // mean reloading the page. When the scene take leaves, the next one takes
    // over — and it must drop its mask, because the scene layer has none.
    if (i < 0 || i >= P.clips.length) return;
    if (P.clips.length === 1) { /* keep the tool alive with at least the intake */ }
    var c = P.clips[i];
    // ⚠️ Do NOT revoke the object URL here. Removal is undoable, and a snapshot
    // that brings the take back gets an object whose blob URL has already been
    // torn down — the settings return, the picture does not. Revocation belongs
    // to gcClips(), which runs only once no snapshot can reach the take again.
    prev.detach(c.id);
    delete mattes[c.id]; delete matteEngines[c.id];
    P.clips.splice(i, 1);
    soloIndex = null;
    if (P.clips.length && P.clips[0].mask) P.clips[0].mask.type = 'none';
    sel = Math.max(0, Math.min(sel, P.clips.length - 1));
    if (P.clips.length >= 2 && sel === 0) sel = 1;
    if (!P.clips.length) {
        aspectAdopted = false;
        els.workspace.classList.remove('active');
        // Emptying the project IS "start over". Keeping the draft would let a
        // reload resurrect exactly what the user just deleted.
        if (window.CloneDraft) window.CloneDraft.clear();
        pendingDraft = null;
    }
    commit('clip_removed');
    gcClips();
    invalidateAudio();
    buildLayers(); buildPanels(); tl && tl.render(); renderAll();
}

/* ───────────────────────────── stage layout ───────────────────────────── */

function layoutStage() {
    if (!P.clips.length) return;
    var box = els.stageBox;
    var availW = box.clientWidth || 640;
    var maxH = Math.max(220, Math.min(window.innerHeight * 0.52, 560));

    // The stage shows the OUTPUT, not the composition: a vertical export that
    // could only be judged after saving the file would be a guess, and the
    // whole reframe is a decision about what the viewer sees.
    var out = window.CloneReframe ? window.CloneReframe.outputSize(P) : { w: P.W, h: P.H };
    var scale = Math.min(availW / out.w, maxH / out.h);
    var cssW = Math.round(out.w * scale), cssH = Math.round(out.h * scale);

    // Cap the backing store at 2× — a devicePixelRatio of 3 (every recent
    // iPhone) would otherwise render nine times the pixels for no visible gain
    // and stall the preview loop.
    var dpr = Math.min(2, window.devicePixelRatio || 1);
    // S stays the COMPOSITION scale: masks, seam handles and every stage
    // gesture are expressed in composition pixels, and re-basing them on the
    // output would move the seam under the user's finger in vertical mode.
    els.S = (Math.round(P.W * (cssW / out.w)) / P.W) * dpr;
    els.outScale = (cssW / out.w) * dpr;

    [els.canvas, els.overlay].forEach(function (cv) {
        cv.style.width = cssW + 'px';
        cv.style.height = cssH + 'px';
        var bw = Math.round(out.w * els.outScale), bh = Math.round(out.h * els.outScale);
        if (cv.width !== bw || cv.height !== bh) { cv.width = bw; cv.height = bh; }
    });
    box.style.height = cssH + 'px';
}

/* ───────────────────────────── rendering ───────────────────────────── */

function renderAll() {
    if (!P.clips.length) return;
    // A render with no drawable frame is not a neutral no-op: the renderer opens
    // by filling the background, so it would REPLACE a good frame with a black
    // one. Holding the previous frame is the honest behaviour while a seek is
    // still in flight.
    if (!prev.anyReady()) { renderChrome(); return; }
    window.CloneScene.renderFramed(els.ctx, P, playhead, function (i) { return prev.getFrame(i); }, els.S || 1,
        { mattes: matteMap(), solo: soloIndex });
    renderChrome();
}

/* Chrome: everything the user needs to SEE but the file must not contain. */
function renderChrome() {
    var ctx = els.octx;
    var cw = els.overlay.width, ch = els.overlay.height;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, cw, ch);

    if (showDiff && diffCache) {
        ctx.globalAlpha = 1;
        ctx.imageSmoothingEnabled = true;
        ctx.drawImage(diffCache.canvas, 0, 0, cw, ch);
    }

    var c = P.clips[sel];
    if (!c || sel === 0 || P.clips.length < 2) return;
    if (P.frame && P.frame.mode === 'vertical') {
        // Say why the handle is gone, rather than letting it look broken.
        ctx.save();
        ctx.font = Math.max(11, Math.round(cw / 34)) + 'px system-ui, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillStyle = 'rgba(0,0,0,.55)';
        var msg = t('out_seam_hidden');
        var tw = ctx.measureText(msg).width + 18;
        ctx.fillRect((cw - tw) / 2, ch - 34, tw, 24);
        ctx.fillStyle = 'rgba(255,255,255,.92)';
        ctx.fillText(msg, cw / 2, ch - 17);
        ctx.restore();
        return;
    }
    var m = window.CloneMasks.resolveAt(c.mask, playhead);
    var accent = (getComputedStyle(document.documentElement).getPropertyValue('--accent') || '#6366f1').trim();

    ctx.save();
    ctx.lineWidth = Math.max(1.5, cw / 620);
    ctx.strokeStyle = '#fff';
    ctx.shadowColor = 'rgba(0,0,0,.75)';
    ctx.shadowBlur = 6;

    if (m.type === 'line') {
        var e = window.CloneMasks.lineEndpoints(m.angle || 0, m.pos == null ? 0.5 : m.pos, cw, ch);
        ctx.beginPath();
        ctx.moveTo(e.x1, e.y1); ctx.lineTo(e.x2, e.y2);
        ctx.stroke();
        dot(ctx, e.cx, e.cy, cw, accent);
        // Rotation grip, offset along the normal.
        var gx = e.cx + e.nx * cw * 0.13, gy = e.cy + e.ny * cw * 0.13;
        ctx.setLineDash([4, 4]);
        ctx.beginPath(); ctx.moveTo(e.cx, e.cy); ctx.lineTo(gx, gy); ctx.stroke();
        ctx.setLineDash([]);
        dot(ctx, gx, gy, cw, '#fff');
    } else if (m.type === 'rect' || m.type === 'ellipse') {
        var w = m.w * cw, h = m.h * ch, x = m.x * cw - w / 2, y = m.y * ch - h / 2;
        ctx.beginPath();
        if (m.type === 'rect') ctx.rect(x, y, w, h);
        else ctx.ellipse(m.x * cw, m.y * ch, Math.max(1, w / 2), Math.max(1, h / 2), 0, 0, Math.PI * 2);
        ctx.stroke();
        [[x, y], [x + w, y], [x, y + h], [x + w, y + h]].forEach(function (p) { dot(ctx, p[0], p[1], cw, accent); });
    }
    ctx.restore();
}

function dot(ctx, x, y, cw, color) {
    var r = Math.max(5, cw / 110);
    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,.6)'; ctx.shadowBlur = 5;
    ctx.fillStyle = color;
    ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill();
    ctx.lineWidth = 1.5; ctx.strokeStyle = 'rgba(255,255,255,.9)'; ctx.shadowBlur = 0;
    ctx.stroke();
    ctx.restore();
}

/* ───────────────────────────── stage gestures ─────────────────────────────
   Coordinates are converted through the live bounding rect into the OVERLAY's
   bitmap space, which is the same space the mask paths are built in. Deriving
   the ratio from canvas.width / rect.width would be device pixels and would
   put every hit test at the wrong place as soon as the preview is not 1:1. */

function bindStage() {
    var ov = els.overlay;
    var drag = null;

    function pt(e) {
        var r = ov.getBoundingClientRect();
        var sx = ov.width / Math.max(1, r.width);
        var sy = ov.height / Math.max(1, r.height);
        return { x: (e.clientX - r.left) * sx, y: (e.clientY - r.top) * sy, w: ov.width, h: ov.height };
    }

    ov.addEventListener('pointerdown', function (e) {
        var c = P.clips[sel];
        if (!c || P.clips.length < 2) return;
        // Vertical framing shows a crop, so a drag here would move the seam by
        // a different amount than the finger travelled.
        if (P.frame && P.frame.mode === 'vertical') return;
        var p = pt(e);
        // Capture the FIRST pointer only. Capturing a second one makes the
        // browser fire lostpointercapture for the first, which reads as "finger
        // lifted" and kills the gesture that the second finger started.
        if (!drag) { try { ov.setPointerCapture(e.pointerId); } catch (_) {} }

        if (editMode === 'layer') {
            drag = { kind: 'layer', x0: p.x, y0: p.y, dx0: c.dx, dy0: c.dy };
        } else {
            drag = hitMask(c, p);
            // A TAP has no pointermove, so a seam that only updates on move does
            // nothing at all when touched — and tapping where you want the seam
            // is the obvious gesture on a phone. Place it on contact, exactly
            // like a scrubber; the drag then refines it.
            if (drag && drag.kind === 'pos') {
                c.mask.pos = window.CloneMasks.posFromPoint(c.mask.angle || 0, p.w, p.h, p.x, p.y);
                renderAll();
            }
        }
        if (drag) e.preventDefault();
    });

    ov.addEventListener('pointermove', function (e) {
        if (!drag) return;
        var c = P.clips[sel];
        var p = pt(e);
        var m = c.mask;

        if (drag.kind === 'layer') {
            var k = P.W / p.w;
            c.dx = drag.dx0 + (p.x - drag.x0) * k;
            c.dy = drag.dy0 + (p.y - drag.y0) * k;
        } else if (drag.kind === 'pos') {
            m.pos = window.CloneMasks.posFromPoint(m.angle || 0, p.w, p.h, p.x, p.y);
        } else if (drag.kind === 'angle') {
            var e2 = window.CloneMasks.lineEndpoints(m.angle || 0, m.pos, p.w, p.h);
            m.angle = Math.atan2(p.y - e2.cy, p.x - e2.cx) * 180 / Math.PI;
            if (e.shiftKey) m.angle = Math.round(m.angle / 15) * 15;
        } else if (drag.kind === 'move') {
            m.x = Math.max(0, Math.min(1, drag.mx0 + (p.x - drag.x0) / p.w));
            m.y = Math.max(0, Math.min(1, drag.my0 + (p.y - drag.y0) / p.h));
        } else if (drag.kind === 'resize') {
            m.w = Math.max(0.03, Math.min(2, drag.mw0 + (p.x - drag.x0) / p.w * 2 * drag.sx));
            m.h = Math.max(0.03, Math.min(2, drag.mh0 + (p.y - drag.y0) / p.h * 2 * drag.sy));
        } else if (drag.kind === 'brush') {
            window.CloneMasks.pushStrokePoint(drag.stroke, p.x / p.w, p.y / p.h, 0.18 * drag.stroke.w);
        }
        // read-outs refresh on gesture end — see endDrag
        renderAll();
    });

    function endDrag(e) {
        if (!drag) return;
        var was = drag.kind;
        drag = null;
        try { ov.releasePointerCapture(e.pointerId); } catch (_) {}
        // Panel read-outs are refreshed once, HERE, rather than on every
        // pointermove: rebuilding the panel at 60 Hz drops focus and thrashes
        // the DOM, but leaving the numbers stale after a stage drag makes the
        // sliders lie about the state they are supposed to show.
        if (was !== 'brush') buildPanels();
        if (was === 'pos' || was === 'angle') noteSeamCorrection('drag');
        commit('stage_' + was);
        ae('feature_use', { f: 'stage_edit', kind: was, mask: (P.clips[sel] && P.clips[sel].mask.type) || '' });
    }
    ov.addEventListener('pointerup', endDrag);
    ov.addEventListener('pointercancel', endDrag);
    // lostpointercapture is deliberately NOT treated as an end-of-gesture.
}

function hitMask(c, p) {
    var m = c.mask;
    var grab = Math.max(14, p.w / 45);

    if (m.type === 'line') {
        var e = window.CloneMasks.lineEndpoints(m.angle || 0, m.pos == null ? 0.5 : m.pos, p.w, p.h);
        var gx = e.cx + e.nx * p.w * 0.13, gy = e.cy + e.ny * p.w * 0.13;
        if (Math.hypot(p.x - gx, p.y - gy) < grab) return { kind: 'angle' };
        return { kind: 'pos' };
    }
    if (m.type === 'rect' || m.type === 'ellipse') {
        var w = m.w * p.w, h = m.h * p.h;
        var cx = m.x * p.w, cy = m.y * p.h;
        var corners = [[cx - w / 2, cy - h / 2, -1, -1], [cx + w / 2, cy - h / 2, 1, -1],
                       [cx - w / 2, cy + h / 2, -1, 1], [cx + w / 2, cy + h / 2, 1, 1]];
        for (var i = 0; i < corners.length; i++) {
            if (Math.hypot(p.x - corners[i][0], p.y - corners[i][1]) < grab) {
                return { kind: 'resize', x0: p.x, y0: p.y, mw0: m.w, mh0: m.h, sx: corners[i][2], sy: corners[i][3] };
            }
        }
        return { kind: 'move', x0: p.x, y0: p.y, mx0: m.x, my0: m.y };
    }
    if (m.type === 'brush') {
        var st = { pts: [], w: m.__brushW || 0.10, erase: !!m.__erase };
        m.strokes.push(st);
        window.CloneMasks.pushStrokePoint(st, p.x / p.w, p.y / p.h, 0);
        return { kind: 'brush', stroke: st };
    }
    return null;
}

/* ───────────────────────────── transport ───────────────────────────── */

function buildTransport() {
    var host = els.transport;
    host.innerHTML = '';

    els.playBtn = button('▶', 'ce-play', togglePlay);
    els.playBtn.setAttribute('aria-label', t('play'));
    host.appendChild(els.playBtn);

    els.seek = el('input', 'ce-seek');
    els.seek.type = 'range'; els.seek.min = 0; els.seek.max = 1000; els.seek.value = 0;
    els.seek.setAttribute('aria-label', t('seek'));
    els.seek.addEventListener('input', function () {
        var d = window.CloneScene.projectDuration(P);
        seekTo(d * (parseFloat(els.seek.value) / 1000));
    });
    host.appendChild(els.seek);

    els.timeLabel = el('span', 'ce-time', '0:00 / 0:00');
    host.appendChild(els.timeLabel);
}

function updateTransport() {
    var d = window.CloneScene.projectDuration(P);
    els.timeLabel.textContent = fmtTime(playhead) + ' / ' + fmtTime(d);
    if (document.activeElement !== els.seek) {
        els.seek.value = d > 0 ? Math.round(playhead / d * 1000) : 0;
    }
    tl && tl.setTime(playhead);
}

var seekSeq = 0;
function seekTo(t2) {
    var d = window.CloneScene.projectDuration(P);
    playhead = Math.max(0, Math.min(t2, d));
    if (playing) restartMixAt(playhead);
    updateTransport();

    // Draw only once the elements have actually presented the new frame. The
    // sequence number drops stale seeks: dragging the scrubber fires dozens,
    // and an early one landing last would repaint an old moment.
    var mine = ++seekSeq;
    prev.sync(playhead, playing, true).then(function () {
        if (mine !== seekSeq) return;
        refreshMattes(true);
        renderAll();
    });
}

async function togglePlay() {
    if (playing) { stopPlayback(); return; }
    var d = window.CloneScene.projectDuration(P);
    if (!(d > 0)) return;
    if (playhead >= d - 0.05) playhead = 0;

    playing = true;
    els.playBtn.textContent = '⏸';
    els.playBtn.setAttribute('aria-label', t('pause'));

    // Build the mix on first play rather than on every slider move: it is the
    // real mix (see the module header), so it must be current, but rebuilding
    // it while someone drags a volume fader would stall the UI on every pixel.
    await ensureMix();
    if (!playing) return;

    await prev.sync(playhead, true, true);
    if (!playing) return;
    startMixAt(playhead);
    loop();
    stage('previewed');
    ae('feature_use', { f: 'preview_play', dur: Math.round(d) });
}

function stopPlayback() {
    playing = false;
    if (els.playBtn) { els.playBtn.textContent = '▶'; els.playBtn.setAttribute('aria-label', t('play')); }
    prev.pauseAll();
    stopMix();
    if (rafId) { cancelAnimationFrame(rafId); rafId = 0; }
}

function loop() {
    rafId = requestAnimationFrame(function () {
        if (!playing) return;
        var d = window.CloneScene.projectDuration(P);
        // The audio clock is the master when there is audio: video elements
        // drift, an AudioContext does not, and drift is what makes a dialogue
        // edit look mistimed on playback but correct in the file.
        if (actx && mixNode) playhead = mixStartOffset + (actx.currentTime - mixStartedAt);
        else playhead += 1 / 60;

        if (playhead >= d) { playhead = d; stopPlayback(); updateTransport(); renderAll(); return; }
        prev.sync(playhead, true, false);
        refreshMattes(false);
        updateTransport();
        renderAll();
        loop();
    });
}

/* ───────────────────────── preview audio (the real mix) ───────────────────── */

function invalidateAudio() { mixState.dirty = true; mixState.buffer = null; }

async function ensureMix() {
    if (!mixState.dirty && mixState.buffer) return mixState.buffer;
    if (mixState.building) return mixState.buffer;
    mixState.building = true;
    setBusy(true, t('busy_audio'));
    try {
        mixState.buffer = await window.CloneAudio.buildMix(P, null, null);
        mixState.dirty = false;
    } catch (e) {
        ae('feature_use', { f: 'preview_mix_failed', err: ((e && e.message) || '').slice(0, 50) });
    } finally {
        mixState.building = false;
        setBusy(false);
    }
    return mixState.buffer;
}

function startMixAt(from) {
    stopMix();
    if (!mixState.buffer) return;
    try {
        if (!actx) actx = new (window.AudioContext || window.webkitAudioContext)();
        if (actx.state === 'suspended') actx.resume();
        mixNode = actx.createBufferSource();
        mixNode.buffer = mixState.buffer;
        mixNode.connect(actx.destination);
        mixStartedAt = actx.currentTime;
        mixStartOffset = from;
        mixNode.start(0, Math.max(0, from));
    } catch (_) { mixNode = null; }
}

function restartMixAt(from) { if (playing) startMixAt(from); }

function stopMix() {
    if (mixNode) { try { mixNode.stop(); } catch (_) {} try { mixNode.disconnect(); } catch (_) {} mixNode = null; }
}

/* ───────────────────────────── silhouette mattes ─────────────────────────────
   Recomputed at most ~12 times a second in the preview: a silhouette is a
   per-frame measurement, but at 60 Hz it would eat the frame budget and the
   difference between 12 and 60 Hz is invisible while scrubbing. */

function refreshMattes(force) {
    var now = performance.now();
    if (!force && now - lastMatteAt < 80) return;
    lastMatteAt = now;

    for (var i = 1; i < P.clips.length; i++) {
        var c = P.clips[i];
        if (!c || !c.mask) continue;
        var id = c.id;
        if (c.mask.type === 'auto') {
            var eng = matteEngines[id];
            if (!eng) continue;
            var f = prev.getFrame(i);
            if (f) mattes[id] = eng.compute(f);
        } else if (c.mask.type === 'ai') {
            var eng2 = matteEngines[id];
            var f2 = prev.getFrame(i);
            if (eng2 && f2 && !eng2.__busy) {
                eng2.__busy = true;
                eng2.compute(f2, c.vw, c.vh, false).then(function (key) {
                    return function (m) { mattes[key] = m; matteEngines[key].__busy = false; renderAll(); };
                }(id)).catch(function (key) {
                    return function () { if (matteEngines[key]) matteEngines[key].__busy = false; };
                }(id));
            }
        } else {
            delete mattes[id];
        }
    }
}

/* scene.render() addresses layers by index, so the id-keyed store is projected
   onto indices at the moment of drawing — the map stays correct across removals
   and the renderer keeps its simple contract. */
function matteMap() {
    var out = {};
    for (var i = 0; i < P.clips.length; i++) {
        var m = mattes[P.clips[i].id];
        if (m) out[i] = m;
    }
    return out;
}

/* ─────────────────────────────── timeline ─────────────────────────────── */

function buildTimeline() {
    var cv = $('ceTimelineCanvas');
    if (!cv) return;
    tl = new window.CloneTimeline.Timeline(cv, P, {
        clipLabel: function (i) { return t('layer_n').replace(':n', i + 1); },
        musicLabel: t('music'),
        onScrub: function (x) { seekTo(x); },
        onSelect: function (i) { if (i > 0) { sel = i; buildLayers(); buildPanels(); } renderAll(); },
        onChange: function () { updateTransport(); renderAll(); },
        onCommit: function () { invalidateAudio(); computeSpans(); commit('timeline'); }
    });
}

/* Speech spans for the timeline + the arrange button. Decoding is cached by the
   audio module, so this is cheap on every call after the first. */
async function computeSpans() {
    if (!P.clips.length) return;           // nothing to listen to yet
    var octx = window.CloneAudio.offlineCtx(1, window.CloneAudio.SR);
    if (!octx) return;
    for (var i = 0; i < P.clips.length; i++) {
        var c = P.clips[i];
        if (!c || c.__spans) continue;
        try {
            var buf = await window.CloneAudio.decode(c.file, octx, null);
            if (!buf) { c.hasAudio = false; c.__spans = []; continue; }
            c.__spans = window.CloneAudio.speechSpans(buf);
            c.__buf = buf;
            tl && tl.setSpans(i, c.__spans);
        } catch (_) { c.__spans = []; }
    }
    tl && tl.render();
}

/* ─────────────────────────────── panels ─────────────────────────────── */

function buildTabs() {
    var host = els.tabs;
    host.innerHTML = '';
    var tabs = [
        { id: 'seam', label: t('tab_seam') },
        { id: 'frame', label: t('tab_frame') },
        { id: 'voices', label: t('tab_voices') },
        { id: 'music', label: t('tab_music') },
        { id: 'export', label: t('tab_export') }
    ];
    tabs.forEach(function (tb) {
        var b = el('button', 'ce-tab' + (tb.id === panel ? ' active' : ''), tb.label);
        b.type = 'button';
        b.setAttribute('role', 'tab');
        b.setAttribute('aria-selected', tb.id === panel ? 'true' : 'false');
        b.addEventListener('click', function () {
            panel = tb.id;
            host.querySelectorAll('.ce-tab').forEach(function (x) {
                x.classList.toggle('active', x === b);
                x.setAttribute('aria-selected', x === b ? 'true' : 'false');
            });
            buildPanels();
            ae('feature_use', { f: 'panel_open', p: tb.id });
        });
        host.appendChild(b);
    });
}

/* Some panel content is Blade-rendered and must SURVIVE a panel rebuild: the
   music uploader, the format select and the shared limit block are components,
   not markup this file owns. They are parked back in a hidden stash before the
   panel is cleared — innerHTML='' would destroy them and the tool would lose
   its uploader the first time someone switched tabs. */
var MOUNTS = ['ceMusicMount', 'ceExportMount', 'ceLimitMount'];

function parkMounts() {
    var stash = $('ceStash');
    if (!stash) return;
    MOUNTS.forEach(function (id) {
        var n = $(id);
        if (n && n.parentNode !== stash) { n.hidden = true; stash.appendChild(n); }
    });
}

function mount(host, id) {
    var n = $(id);
    if (!n) return;
    n.hidden = false;
    host.appendChild(n);
}

function buildPanels() {
    var host = els.panels;
    if (!host) return;
    parkMounts();
    host.innerHTML = '';
    if (P.clips.length < 1) return;

    if (panel === 'seam') buildSeamPanel(host);
    else if (panel === 'frame') buildFramePanel(host);
    else if (panel === 'voices') buildVoicesPanel(host);
    else if (panel === 'music') buildMusicPanel(host);
    else if (panel === 'export') buildExportPanel(host);
}

function needTwo(host) {
    if (P.clips.length >= 2) return false;
    host.appendChild(el('div', 'ce-empty', t('need_two')));
    return true;
}

/* ── Seam ─────────────────────────────────────────────────────────────── */

function buildSeamPanel(host) {
    if (needTwo(host)) return;
    var c = P.clips[sel];
    var m = c.mask;

    /* A see-through double. Full opacity is a second person standing in the
       room; below that the take reads as a ghost, and it is the only way to
       have the two of you OVERLAP on purpose — a hard mask cannot do that at
       all. Only offered on a pasted take: fading the scene layer would just
       fade the whole picture. */
    if (sel > 0) {
        var opV = el('span', 'ce-val', Math.round((c.opacity == null ? 1 : c.opacity) * 100) + '%');
        host.appendChild(row(t('clone_opacity'), wrapVal(slider(20, 100, 1,
            (c.opacity == null ? 1 : c.opacity) * 100, function (v) {
                c.opacity = v / 100;
                opV.textContent = Math.round(v) + '%';
                renderAll();
            }), opV), t('clone_opacity_hint')));
    }

    host.appendChild(row(t('mask_kind'), chipStrip([
        { id: 'line', label: t('mask_line'), title: t('mask_line_hint') },
        { id: 'rect', label: t('mask_rect') },
        { id: 'ellipse', label: t('mask_ellipse') },
        { id: 'brush', label: t('mask_brush') },
        { id: 'auto', label: t('mask_auto'), title: t('mask_auto_hint') },
        { id: 'ai', label: t('mask_ai'), title: t('mask_ai_hint') }
    ], m.type, function (id) { setMaskType(c, id); })));

    if (m.type === 'line') {
        var posV = el('span', 'ce-val', Math.round(m.pos * 100) + '%');
        host.appendChild(row(t('seam_pos'), wrapVal(slider(0, 100, 0.5, m.pos * 100, function (v) {
            m.pos = v / 100; posV.textContent = Math.round(v) + '%'; renderAll();
            noteSeamCorrection('slider');
        }), posV)));

        var angV = el('span', 'ce-val', Math.round(m.angle) + '°');
        host.appendChild(row(t('seam_angle'), wrapVal(slider(-90, 90, 1, m.angle, function (v) {
            m.angle = v; angV.textContent = Math.round(v) + '°'; renderAll();
            noteSeamCorrection('slider');
        }), angV)));

        var quick = el('div', 'ce-chips');
        [[0, t('seam_vertical')], [90, t('seam_horizontal')]].forEach(function (q) {
            quick.appendChild(button(q[1], 'ghost', function () {
                m.angle = q[0]; buildPanels(); renderAll();
            }));
        });
        host.appendChild(row('', quick));

            host.appendChild(actionRow(button('✨ ' + t('seam_suggest'), 'primary', suggestSeam), t('seam_suggest_hint')));
        host.appendChild(buildKeyframeBlock(c, m));
    }

    if (m.type === 'rect' || m.type === 'ellipse') {
        var wV = el('span', 'ce-val', Math.round(m.w * 100) + '%');
        host.appendChild(row(t('shape_w'), wrapVal(slider(3, 150, 1, m.w * 100, function (v) {
            m.w = v / 100; wV.textContent = Math.round(v) + '%'; renderAll();
        }), wV)));
        var hV = el('span', 'ce-val', Math.round(m.h * 100) + '%');
        host.appendChild(row(t('shape_h'), wrapVal(slider(3, 150, 1, m.h * 100, function (v) {
            m.h = v / 100; hV.textContent = Math.round(v) + '%'; renderAll();
        }), hV)));
        if (m.type === 'rect') {
            var rV = el('span', 'ce-val', Math.round((m.radius || 0) * 100) + '%');
            host.appendChild(row(t('shape_radius'), wrapVal(slider(0, 50, 1, (m.radius || 0) * 100, function (v) {
                m.radius = v / 100; rV.textContent = Math.round(v) + '%'; renderAll();
            }), rV)));
        }
    }

    if (m.type === 'brush') {
        m.__brushW = m.__brushW || 0.10;
        var bV = el('span', 'ce-val', Math.round(m.__brushW * 100) + '%');
        host.appendChild(row(t('brush_size'), wrapVal(slider(2, 40, 1, m.__brushW * 100, function (v) {
            m.__brushW = v / 100; bV.textContent = Math.round(v) + '%';
        }), bV)));
        var modeStrip = chipStrip([
            { id: 'paint', label: t('brush_paint') },
            { id: 'erase', label: t('brush_erase') }
        ], m.__erase ? 'erase' : 'paint', function (id) { m.__erase = (id === 'erase'); });
        host.appendChild(row(t('brush_mode'), modeStrip));
        host.appendChild(actionRow(button(t('brush_clear'), 'ghost', function () {
            m.strokes = []; renderAll();
        }), t('brush_hint')));
    }

    if (m.type === 'auto' || m.type === 'ai') {
        host.appendChild(el('div', 'ce-hint', m.type === 'auto' ? t('mask_auto_desc') : t('mask_ai_desc')));
        if (m.type === 'auto') {
            var engNow = matteEngines[c.id];
            var thrV = el('span', 'ce-val', String(Math.round((engNow && engNow.threshold) || 22)));
            host.appendChild(row(t('auto_sensitivity'), wrapVal(slider(6, 60, 1, (engNow && engNow.threshold) || 22, function (v) {
                if (matteEngines[c.id]) matteEngines[c.id].threshold = v;
                thrV.textContent = String(Math.round(v));
                refreshMattes(true); renderAll();
            }), thrV)));
            host.appendChild(actionRow(button(t('auto_rebuild'), 'ghost', function () { buildPlateFor(sel, true); }), ''));
        }
    }

    if (m.type !== 'auto' && m.type !== 'ai') {
        var fV = el('span', 'ce-val', Math.round(m.feather) + ' px');
        host.appendChild(row(t('feather'), wrapVal(slider(0, 60, 1, m.feather, function (v) {
            m.feather = v; fV.textContent = Math.round(v) + ' px'; renderAll();
        }), fV), t('feather_hint')));

        host.appendChild(row(t('invert'), toggle(m.invert, function (v) {
            m.invert = v; renderAll(); noteSeamCorrection('swap');
        })));
    }

    host.appendChild(row(t('diff_map'), toggle(showDiff, function (v) {
        showDiff = v;
        if (v) refreshDiff();
        renderChrome();
        ae('feature_use', { f: 'diff_map', on: v ? 1 : 0 });
    }), t('diff_map_hint')));
}

function buildKeyframeBlock(c, m) {
    var wrap = el('div', 'ce-sub');
    wrap.appendChild(el('div', 'ce-sub-title', t('keys_title')));
    wrap.appendChild(el('div', 'ce-hint', t('keys_hint')));

    var list = el('div', 'ce-keys');
    (m.keys || []).slice().sort(function (a, b) { return a.t - b.t; }).forEach(function (k) {
        var chip = el('button', 'ce-chip small', fmtTime(k.t));
        chip.type = 'button';
        chip.title = t('keys_remove');
        chip.addEventListener('click', function () {
            m.keys = m.keys.filter(function (x) { return x !== k; });
            buildPanels(); renderAll();
        });
        list.appendChild(chip);
    });
    if (!(m.keys || []).length) list.appendChild(el('span', 'ce-hint', t('keys_none')));

    var add = button('＋ ' + t('keys_add'), 'ghost', function () {
        var live = window.CloneMasks.resolveAt(m, playhead);
        m.keys = (m.keys || []).filter(function (k) { return Math.abs(k.t - playhead) > 0.05; });
        m.keys.push({ t: playhead, pos: live.pos, angle: live.angle });
        buildPanels(); renderAll();
        ae('feature_use', { f: 'seam_key_add', n: m.keys.length });
    });
    var clear = button(t('keys_clear'), 'ghost', function () { m.keys = []; buildPanels(); renderAll(); });

    var actions = el('div', 'ce-chips');
    actions.appendChild(add);
    if ((m.keys || []).length) actions.appendChild(clear);

    wrap.appendChild(list);
    wrap.appendChild(actions);
    return wrap;
}

function setMaskType(c, id) {
    var prevType = c.mask.type;
    c.mask.type = id;
    delete mattes[c.id];

    if (id === 'auto') {
        buildPlateFor(sel, false);
    } else if (id === 'ai') {
        enableAiFor(sel);
    } else {
        delete matteEngines[c.id];
    }
    ae('feature_use', { f: 'mask_type', from: prevType, to: id });
    buildPanels();
    renderAll();
    commit('mask_type');
}

async function buildPlateFor(i, force) {
    var c = P.clips[i];
    if (!c) return;
    if (c.__plate && !force) {
        matteEngines[c.id] = new window.CloneMatte.PlateMatte(c.__plate);
        refreshMattes(true); renderAll();
        return;
    }
    setBusy(true, t('busy_plate'));
    try {
        var plate = await window.CloneMatte.buildPlate(c, null, null);
        if (!plate) { toast(t('err_plate'), 'error'); return; }
        c.__plate = plate;
        matteEngines[c.id] = new window.CloneMatte.PlateMatte(plate);
        refreshMattes(true);
        renderAll();

        // Honesty check: a performer who never moved IS the median, so the
        // silhouette comes out empty. Say that instead of showing a blank layer.
        var cov = matteEngines[c.id].coverage();
        ae('feature_use', { f: 'plate_built', cov: Math.round(cov * 100) });
        if (cov < 0.005) toast(t('warn_plate_empty'), 'warning');
        else if (cov > 0.6) toast(t('warn_plate_busy'), 'warning');
    } catch (e) {
        toast(t('err_plate'), 'error');
    } finally { setBusy(false); }
}

/* Loaded on demand — see the note next to __CE_RVM_URL in the blade. */
function loadRvm() {
    if (window.VbrRvmEngine) return Promise.resolve(true);
    if (!window.__CE_RVM_URL) return Promise.resolve(false);
    if (window.__ceRvmLoading) return window.__ceRvmLoading;
    window.__ceRvmLoading = new Promise(function (resolve) {
        var sc = document.createElement('script');
        sc.src = window.__CE_RVM_URL;
        sc.onload = function () { resolve(!!window.VbrRvmEngine); };
        sc.onerror = function () { resolve(false); };
        document.head.appendChild(sc);
    });
    return window.__ceRvmLoading;
}

async function enableAiFor(i) {
    setBusy(true, t('busy_model'));
    var got = await loadRvm();
    if (!got || !window.CloneMatte.AiMatte.available()) {
        setBusy(false);
        toast(t('err_ai_missing'), 'error');
        P.clips[i].mask.type = 'line';
        buildPanels(); renderAll();
        return;
    }
    var eng = new window.CloneMatte.AiMatte();
    try {
        await eng.ready(null);
        matteEngines[P.clips[i].id] = eng;
        refreshMattes(true);
        renderAll();
    } catch (e) {
        toast(t('err_ai_load'), 'error');
        P.clips[i].mask.type = 'line';
        buildPanels();
    } finally { setBusy(false); }
}

function refreshDiff() {
    if (P.clips.length < 2) return;
    var a = prev.getFrame(0), b = prev.getFrame(sel);
    if (!a || !b) return;
    diffCache = window.CloneAlign.differenceMap(a, b, 14);
}

function suggestSeam() {
    refreshDiff();
    if (!diffCache) { toast(t('err_no_frames'), 'warning'); return; }
    var c = P.clips[sel];
    var pos = window.CloneAlign.suggestSeam(diffCache.field, diffCache.w, diffCache.h, c.mask.angle || 0);
    c.mask.pos = pos;
    buildPanels();
    renderAll();
    commit('seam_suggest');
    toast(t('seam_suggested'), 'success');
    ae('feature_use', { f: 'seam_suggest', pos: Math.round(pos * 100) });
}

/* ── Frame ────────────────────────────────────────────────────────────── */

function buildFramePanel(host) {
    var presets = [
        { id: '16:9', label: '16:9', w: 1920, h: 1080 },
        { id: '9:16', label: '9:16', w: 1080, h: 1920 },
        { id: '1:1', label: '1:1', w: 1080, h: 1080 },
        { id: '4:5', label: '4:5', w: 1080, h: 1350 }
    ];
    host.appendChild(row(t('aspect'), chipStrip(presets.map(function (p) { return { id: p.id, label: p.label }; }), P.preset, function (id) {
        var p = presets.filter(function (x) { return x.id === id; })[0];
        P.preset = id;
        applyOutputSize();
        ae('feature_use', { f: 'aspect', v: id });
    })));

    if (needTwo(host)) return;
    var c = P.clips[sel];

    host.appendChild(row(t('edit_mode'), chipStrip([
        { id: 'seam', label: t('edit_mode_seam') },
        { id: 'layer', label: t('edit_mode_layer') }
    ], editMode, function (id) { editMode = id; }), t('edit_mode_hint')));

    host.appendChild(el('div', 'ce-sub-title', t('align_title')));
    host.appendChild(el('div', 'ce-hint', t('align_hint')));

    var actions = el('div', 'ce-chips');
    actions.appendChild(button('🎯 ' + t('align_auto'), 'primary', function () { runAlign(true); }));
    actions.appendChild(button('🎨 ' + t('tone_match'), 'ghost', runToneMatch));
    actions.appendChild(button(t('align_reset'), 'ghost', function () {
        c.dx = 0; c.dy = 0; c.scale = 1; c.exposure = 1; c.tint = [1, 1, 1]; c.offset = [0, 0, 0];
        buildPanels(); renderAll();
    }));
    host.appendChild(actions);

    els.nudgeX = el('span', 'ce-val', Math.round(c.dx) + ' px');
    host.appendChild(row(t('nudge_x'), wrapVal(slider(-200, 200, 1, c.dx, function (v) {
        c.dx = v; els.nudgeX.textContent = Math.round(v) + ' px'; renderAll();
    }), els.nudgeX)));
    els.nudgeY = el('span', 'ce-val', Math.round(c.dy) + ' px');
    host.appendChild(row(t('nudge_y'), wrapVal(slider(-200, 200, 1, c.dy, function (v) {
        c.dy = v; els.nudgeY.textContent = Math.round(v) + ' px'; renderAll();
    }), els.nudgeY)));
    els.nudgeS = el('span', 'ce-val', (c.scale * 100).toFixed(0) + '%');
    host.appendChild(row(t('nudge_scale'), wrapVal(slider(80, 130, 0.5, c.scale * 100, function (v) {
        c.scale = v / 100; els.nudgeS.textContent = Math.round(v) + '%'; renderAll();
    }), els.nudgeS)));

    host.appendChild(row(t('mirror'), toggle(c.flipH, function (v) { c.flipH = v; renderAll(); }), t('mirror_hint')));
    host.appendChild(row(t('fit'), chipStrip([
        { id: 'cover', label: t('fit_cover') },
        { id: 'contain', label: t('fit_contain') }
    ], c.fit, function (id) { c.fit = id; renderAll(); })));
}

function runAlign(announce) {
    var a = prev.getFrame(0), b = prev.getFrame(sel);
    if (!a || !b) { toast(t('err_no_frames'), 'warning'); return; }
    var c = P.clips[sel];
    var r = window.CloneAlign.registration(a, b, P.W);
    ae('feature_use', { f: 'auto_align', dx: r.dx, dy: r.dy, conf: Math.round(r.confidence * 100), why: r.reason || '' });

    // Confidence gates the WRITE, not just the message. Applying a low-confidence
    // shift "just in case" is how a tool moves a layer 108 px because two frames
    // it could not read scored identically.
    if (r.confidence < 0.15 || (r.dx === 0 && r.dy === 0)) {
        if (announce) toast(t('align_already'), 'info');
        return;
    }
    c.dx = r.dx; c.dy = r.dy;
    buildPanels(); renderAll(); commit('align');
    if (announce) toast(t('align_done').replace(':x', r.dx).replace(':y', r.dy), 'success');
}

function runToneMatch() {
    var a = prev.getFrame(0), b = prev.getFrame(sel);
    if (!a || !b) { toast(t('err_no_frames'), 'warning'); return; }
    var r = window.CloneAlign.toneMatch(a, b);
    if (!r) { toast(t('tone_no_background'), 'warning'); return; }
    var c = P.clips[sel];
    c.exposure = r.exposure; c.tint = r.tint; c.offset = r.offset;
    renderAll(); commit('tone');
    toast(t('tone_done').replace(':pct', Math.round((r.exposure - 1) * 100)), 'success');
    ae('feature_use', { f: 'tone_match', exp: Math.round(r.exposure * 100), cov: Math.round(r.coverage * 100) });
}

/* ── Voices ───────────────────────────────────────────────────────────── */

function buildVoicesPanel(host) {
    if (!P.clips.length) return;

    var actions = el('div', 'ce-chips');
    actions.appendChild(button('🗣 ' + t('arrange_turns'), 'primary', arrangeTurns));
    actions.appendChild(button('✂️ ' + t('trim_dead'), 'ghost', trimDeadAir));
    actions.appendChild(button('🎯 ' + t('sync_cue'), 'ghost', syncByCue));
    host.appendChild(actions);
    host.appendChild(el('div', 'ce-hint', t('arrange_hint')));
    host.appendChild(el('div', 'ce-hint', t('trim_hint')));

    /* The pause between one line and the answer is the RHYTHM of the dialogue,
       and it is personal — a comic beat is not a documentary beat. It was a
       hardcoded 0.35 s, which is a decision the tool was making on the user's
       behalf without telling them. */
    var gapV = el('span', 'ce-val', turnGap.toFixed(2) + ' ' + t('sec'));
    host.appendChild(row(t('turn_gap'), wrapVal(slider(-0.5, 2, 0.05, turnGap, function (v) {
        turnGap = v;
        gapV.textContent = (v > 0 ? '+' : '') + v.toFixed(2) + ' ' + t('sec');
        savePref('turnGap', v);
    }), gapV), t('turn_gap_hint')));

    for (var i = 0; i < P.clips.length; i++) {
        (function (i) {
            var c = P.clips[i];
            var box = el('div', 'ce-voice');
            var head = el('div', 'ce-voice-head');
            head.appendChild(el('strong', null, t('layer_n').replace(':n', i + 1)));
            var name = el('span', 'ce-voice-name', c.name);
            head.appendChild(name);
            box.appendChild(head);

            if (!c.hasAudio) {
                box.appendChild(el('div', 'ce-hint', t('no_audio')));
                host.appendChild(box);
                return;
            }

            var mute = el('button', 'ce-mute' + (c.muted ? ' muted' : ''), c.muted ? '🔇' : '🔊');
            mute.type = 'button';
            mute.title = t('mute');
            mute.setAttribute('aria-pressed', c.muted ? 'true' : 'false');
            mute.addEventListener('click', function () {
                c.muted = !c.muted;
                mute.classList.toggle('muted', c.muted);
                mute.textContent = c.muted ? '🔇' : '🔊';
                mute.setAttribute('aria-pressed', c.muted ? 'true' : 'false');
                invalidateAudio(); tl && tl.render();
                ae('feature_use', { f: 'voice_mute', i: i, on: c.muted ? 1 : 0 });
            });

            var vV = el('span', 'ce-val', Math.round(c.volume * 100) + '%');
            var vs = slider(0, 200, 1, c.volume * 100, function (v) {
                c.volume = v / 100; vV.textContent = Math.round(v) + '%'; invalidateAudio();
            });
            var vrow = el('div', 'ce-row');
            vrow.appendChild(el('span', 'ce-row-label', t('volume')));
            vrow.appendChild(vs);
            vrow.appendChild(vV);
            vrow.appendChild(mute);
            box.appendChild(vrow);

            var oV = el('span', 'ce-val', abs(c.start) + ' ' + t('sec'));
            box.appendChild(row(t('position'), wrapVal(slider(0, posMax(), 0.05, c.start, function (v) {
                c.start = v; oV.textContent = abs(v) + ' ' + t('sec');
                invalidateAudio(); tl && tl.render(); updateTransport(); renderAll();
            }), oV), i === 0 ? '' : t('position_hint')));

            var aV = el('span', 'ce-val', signed(c.audioOffset) + ' ' + t('sec'));
            box.appendChild(row(t('av_nudge'), wrapVal(slider(-2, 2, 0.01, c.audioOffset, function (v) {
                c.audioOffset = v; aV.textContent = signed(v) + ' ' + t('sec'); invalidateAudio();
            }), aV), t('av_nudge_hint')));

            host.appendChild(box);
        })(i);
    }
}

function signed(v) { return (v > 0 ? '+' : '') + (Math.round(v * 100) / 100).toFixed(2); }
function abs(v) { return (Math.round(v * 100) / 100).toFixed(2); }

/* The position slider must reach past the current end, otherwise a take can
   never be moved AFTER the composition it currently defines. */
function posMax() {
    var d = window.CloneScene.projectDuration(P);
    return Math.max(20, Math.ceil(d * 1.5));
}

async function arrangeTurns() {
    setBusy(true, t('busy_analyse'));
    try {
        await computeSpans();
        var entries = [];
        for (var i = 0; i < P.clips.length; i++) {
            var c = P.clips[i];
            entries.push({
                clipIndex: i,
                spans: c.__spans || [],
                duration: window.CloneScene.clipDuration(c),
                trimIn: c.trimIn, trimOut: c.trimOut
            });
        }
        var placed = window.CloneAudio.arrangeTurns(entries, turnGap);
        var moved = 0;
        placed.forEach(function (p) {
            var c = P.clips[p.clipIndex];
            if (Math.abs(c.start - p.start) > 0.02) moved++;
            c.start = Math.max(0, p.start);
        });
        invalidateAudio();
        tl && tl.render(); buildPanels(); updateTransport(); renderAll();
        commit('arrange');
        toast(moved ? t('arrange_done') : t('arrange_nothing'), moved ? 'success' : 'info');
        ae('feature_use', { f: 'arrange_turns', moved: moved, n: P.clips.length, gap: Math.round(turnGap * 100) });
    } finally { setBusy(false); }
}

/**
 * Cut the dead air off both ends of every take, then re-arrange.
 *
 * Measured on a two-take fixture: 15.1 s of composition of which 7.8 s was
 * nobody speaking. The catch is that the dead air is also the room's own tone —
 * cut it too close and the result drops to digital silence between the lines,
 * which is a far louder defect than the tone the takes add when layered
 * (+2.2 dB). CloneAudio.planTrims derives the handles from the turn gap so the
 * takes always overlap across the join; where the material cannot cover the
 * requested pause it shortens the pause and says so.
 */
async function trimDeadAir() {
    setBusy(true, t('busy_analyse'));
    try {
        await computeSpans();
        var entries = [], i, c;
        for (i = 0; i < P.clips.length; i++) {
            c = P.clips[i];
            entries.push({
                clipIndex: i,
                spans: c.__spans || [],
                // planTrims works in SOURCE time, so it needs the file's own
                // length — not what an earlier trim left of it. Passing the
                // trimmed length would make the operation eat into the take a
                // little more on every press.
                srcDuration: c.duration,
                duration: window.CloneScene.clipDuration(c)
            });
        }
        var plan = window.CloneAudio.planTrims(entries, turnGap);
        if (!plan.trims.length) {
            toast(t('trim_nothing'), 'info');
            ae('feature_use', { f: 'trim_dead', cut: 0, n: P.clips.length });
            return;
        }
        plan.trims.forEach(function (tr) {
            var cl = P.clips[tr.clipIndex];
            cl.trimIn = tr.trimIn;
            cl.trimOut = tr.trimOut;
        });

        /* Re-arrange with the gap the material can actually carry. */
        var arr = [];
        for (i = 0; i < P.clips.length; i++) {
            c = P.clips[i];
            arr.push({
                clipIndex: i, spans: c.__spans || [],
                duration: window.CloneScene.clipDuration(c),
                trimIn: c.trimIn, trimOut: c.trimOut
            });
        }
        window.CloneAudio.arrangeTurns(arr, plan.gap).forEach(function (p) {
            P.clips[p.clipIndex].start = Math.max(0, p.start);
        });

        // The composition just got shorter; a playhead parked past the new end
        // would leave the stage blank with no obvious reason why.
        if (playhead > window.CloneScene.projectDuration(P)) seekTo(0);
        invalidateAudio();
        tl && tl.render(); buildPanels(); updateTransport(); renderAll();
        commit('trim');
        var secs = plan.removed.toFixed(1);
        toast(plan.capped
            ? t('trim_done_capped').replace(':sec', secs).replace(':gap', plan.gap.toFixed(2))
            : t('trim_done').replace(':sec', secs), 'success');
        ae('feature_use', {
            f: 'trim_dead', cut: Math.round(plan.removed * 10) / 10,
            n: plan.trims.length, capped: plan.capped ? 1 : 0,
            gap: Math.round(plan.gap * 100)
        });
    } finally { setBusy(false); }
}

async function syncByCue() {
    if (P.clips.length < 2) { toast(t('need_two'), 'warning'); return; }
    setBusy(true, t('busy_analyse'));
    try {
        await computeSpans();
        var a = P.clips[0].__buf, b = P.clips[sel].__buf;
        if (!a || !b) { toast(t('err_no_audio'), 'warning'); return; }
        var r = window.CloneAudio.findCommonOffset(a, b, 12);
        ae('feature_use', { f: 'sync_cue', off: Math.round(r.offset * 100), conf: Math.round(r.confidence * 100) });
        if (r.confidence < 0.3) { toast(t('sync_no_cue'), 'warning'); return; }
        P.clips[sel].start = Math.max(0, P.clips[0].start + r.offset);
        invalidateAudio();
        tl && tl.render(); buildPanels(); renderAll();
        toast(t('sync_done').replace(':s', (Math.round(r.offset * 100) / 100).toFixed(2)), 'success');
    } finally { setBusy(false); }
}

/* ── Music ────────────────────────────────────────────────────────────── */

function buildMusicPanel(host) {
    if (!P.music) {
        mount(host, 'ceMusicMount');
        host.appendChild(el('div', 'ce-hint', t('music_hint')));
        return;
    }
    var m = P.music;
    var head = el('div', 'ce-voice-head');
    head.appendChild(el('strong', null, '🎵 ' + t('music')));
    head.appendChild(el('span', 'ce-voice-name', m.name));
    host.appendChild(head);

    var vV = el('span', 'ce-val', Math.round(m.volume * 100) + '%');
    host.appendChild(row(t('volume'), wrapVal(slider(0, 150, 1, m.volume * 100, function (v) {
        m.volume = v / 100; vV.textContent = Math.round(v) + '%'; invalidateAudio();
    }), vV)));

    host.appendChild(row(t('duck'), toggle(m.duck, function (v) {
        m.duck = v; invalidateAudio(); buildPanels();
        ae('feature_use', { f: 'music_duck', on: v ? 1 : 0 });
    }), t('duck_hint')));

    if (m.duck) {
        var dV = el('span', 'ce-val', Math.round(m.duckDepth * 100) + '%');
        host.appendChild(row(t('duck_depth'), wrapVal(slider(20, 95, 1, m.duckDepth * 100, function (v) {
            m.duckDepth = v / 100; dV.textContent = Math.round(v) + '%'; invalidateAudio();
        }), dV)));
    }

    var fiV = el('span', 'ce-val', m.fadeIn.toFixed(1) + ' ' + t('sec'));
    host.appendChild(row(t('fade_in'), wrapVal(slider(0, 8, 0.1, m.fadeIn, function (v) {
        m.fadeIn = v; fiV.textContent = v.toFixed(1) + ' ' + t('sec'); invalidateAudio();
    }), fiV)));
    var foV = el('span', 'ce-val', m.fadeOut.toFixed(1) + ' ' + t('sec'));
    host.appendChild(row(t('fade_out'), wrapVal(slider(0, 8, 0.1, m.fadeOut, function (v) {
        m.fadeOut = v; foV.textContent = v.toFixed(1) + ' ' + t('sec'); invalidateAudio();
    }), foV)));

    host.appendChild(row(t('loop'), toggle(m.loop, function (v) { m.loop = v; invalidateAudio(); tl && tl.render(); })));

    var sV = el('span', 'ce-val', abs(m.start) + ' ' + t('sec'));
    host.appendChild(row(t('position'), wrapVal(slider(0, posMax(), 0.1, m.start, function (v) {
        m.start = v; sV.textContent = abs(v) + ' ' + t('sec'); invalidateAudio(); tl && tl.render();
    }), sV)));

    host.appendChild(actionRow(button(t('music_remove'), 'ghost', function () {
        P.music = null; invalidateAudio(); tl && tl.render(); buildPanels();
    }), ''));
}

function wireMusicUploader() {
    var drop = $('ceMusicDrop');
    if (!drop || !window.__bindUploaderFile) return;
    window.__bindUploaderFile(drop, async function (f) {
        if (!f) return;
        var dur = 0;
        try {
            var octx = window.CloneAudio.offlineCtx(1, window.CloneAudio.SR);
            var buf = await window.CloneAudio.decode(f, octx, null);
            dur = buf ? buf.duration : 0;
            if (!buf) throw new Error('decode');
        } catch (_) {
            toast(t('err_music'), 'error');
            return;
        }
        P.music = window.CloneScene.newMusic(f, { duration: dur });
        invalidateAudio();
        buildPanels(); tl && tl.render();
        ae('feature_use', { f: 'music_added', dur: Math.round(dur) });
    });
}

/* ── output framing ────────────────────────────────────────────────────
   A 16:9 clone composition posted to Reels is the common ending for this tool,
   and the obvious way to get there destroys it: a centre crop keeps NEITHER
   performer (measured 0.00 / 0.00 kept), and following the speaker keeps one
   at a time, which throws away the only thing the video was made to show.

   Stacking keeps both. The numbers behind the window geometry live in
   reframe.js; what happens here is finding WHERE the two performers are, which
   the tool can already answer: the takes share a background, so the field where
   they disagree is exactly where somebody is standing, and the seam says which
   blob is whose. Sampled at several times and unioned, because performers move
   and a frame that fits them at one second can cut them at the next. */

async function analyseFraming() {
    var R = window.CloneReframe;
    var n = P.clips.length;
    if (!R || n < 2) return null;

    var cB = P.clips[1];
    var angle = (cB.mask && cB.mask.angle) || 0;
    var pos = (cB.mask && cB.mask.pos != null) ? cB.mask.pos : 0.5;

    var dur = window.CloneScene.projectDuration(P);
    var samples = [], i, j;
    for (i = 0; i < n; i++) samples.push([]);
    var got = 0;

    for (var k = 1; k <= R.SAMPLES; k++) {
        var at = dur * k / (R.SAMPLES + 1);
        await prev.sync(at, false, true);
        var frames = [], ok = true;
        for (i = 0; i < n; i++) {
            frames.push(prev.getFrame(i));
            if (!frames[i]) ok = false;
        }
        if (!ok) continue;

        var boxes;
        if (n === 2) {
            // Two takes: their difference lights up both performers and the
            // seam says which is which.
            var dm = window.CloneAlign.differenceMap(frames[0], frames[1]);
            if (!dm || !dm.field) continue;
            boxes = R.boxesFromField(dm.field, dm.w, dm.h, angle, pos);
        } else {
            // Three or more: no single seam to split on, so each performer is
            // found as the region where their take differs from EVERY other.
            var fields = {}, fw = 0, fh = 0;
            for (i = 0; i < n; i++) {
                for (j = i + 1; j < n; j++) {
                    var d2 = window.CloneAlign.differenceMap(frames[i], frames[j]);
                    if (!d2 || !d2.field) continue;
                    fields[i + '_' + j] = d2.field; fw = d2.w; fh = d2.h;
                }
            }
            if (!fw) continue;
            boxes = R.performerBoxes(fields, n, fw, fh);
        }
        for (i = 0; i < n; i++) samples[i].push(boxes[i]);
        got++;
    }
    await prev.sync(playhead, false, true);
    if (!got) return null;

    var united = samples.map(function (list) { return R.unionBox(list); });
    var out = R.outputSize({ W: P.W, H: P.H, clips: P.clips, frame: { mode: 'vertical' } });
    return {
        win: R.windowsFor(united, P.W, P.H, R.cellAspect(out, n), angle, pos),
        found: united.filter(Boolean).length,
        want: n
    };
}

async function setFrameMode(mode) {
    if (!P.frame) P.frame = { mode: 'source', win: null };
    if (P.frame.mode === mode && (mode !== 'vertical' || P.frame.win)) return;

    if (mode !== 'vertical') {
        P.frame = { mode: 'source', win: null };
        layoutStage(); renderAll(); buildPanels(); commit('frame');
        ae('feature_use', { f: 'frame_mode', mode: 'source' });
        return;
    }

    setBusy(true, t('busy_frame'));
    try {
        var r = await analyseFraming();
        P.frame = { mode: 'vertical', win: r ? r.win : null };
        layoutStage(); renderAll(); buildPanels(); commit('frame');
        // Say WHICH of the two could not be located: a silent fallback to the
        // centre of the frame looks like a decision until the export lands.
        if (!r || r.found < r.want) toast(t('out_frame_guess'), 'warning');
        else toast(t('out_frame_done'), 'success');
        ae('feature_use', { f: 'frame_mode', mode: 'vertical', found: r ? r.found : 0 });
    } finally { setBusy(false); }
}

/* ── Export ───────────────────────────────────────────────────────────── */

function buildExportPanel(host) {
    if (needTwo(host)) return;

    host.appendChild(row(t('out_frame'), chipStrip([
        { id: 'source', label: t('out_source') },
        { id: 'vertical', label: t('out_vertical') }
    ], (P.frame && P.frame.mode) || 'source', function (id) { setFrameMode(id); })));
    host.appendChild(el('div', 'ce-hint', t('out_frame_hint')));
    if (P.frame && P.frame.mode === 'vertical') {
        // The windows were measured from the composition as it stood. Move the
        // seam or swap a take and they are a snapshot of something that no
        // longer exists — so the recompute is offered rather than left to a
        // mode toggle nobody would think to do twice.
        var again = el('div', 'ce-chips');
        again.appendChild(button('↻ ' + t('out_reframe'), 'ghost', function () {
            P.frame.win = null;
            setFrameMode('vertical');
        }));
        host.appendChild(again);
    }

    host.appendChild(row(t('quality'), chipStrip([
        { id: 'high', label: t('q_high') },
        { id: 'medium', label: t('q_medium') },
        { id: 'low', label: t('q_low') }
    ], P.quality, function (id) { P.quality = id; savePref('quality', id); })));

    host.appendChild(row(t('fps'), chipStrip([
        { id: '30', label: '30 fps' },
        { id: '60', label: '60 fps' }
    ], String(P.fps), function (id) { P.fps = parseInt(id, 10); savePref('fps', id); })));

    // Format, output height, the export button and the progress block are Blade
    // components (registry-driven format list, tier-gated height, the shared
    // limit block's named button). They live in the page from the first byte and
    // are only MOVED here — rebuilding them in JS would fork the format registry
    // and leave the limit block pointing at a button that does not exist yet.
    mount(host, 'ceExportMount');
    mount(host, 'ceLimitMount');
}

/* Preset chooses the SHAPE, the height select chooses the SIZE. Gating the
   presets directly would lock all four (every one is ≥1080 tall) and leave a
   free user with nothing to press — the same split /split-screen settled on. */
function applyOutputSize() {
    var ar = { '16:9': 16 / 9, '9:16': 9 / 16, '1:1': 1, '4:5': 4 / 5 }[P.preset] || 16 / 9;
    var hSel = $('ceHeight');
    var h = hSel ? parseInt(hSel.value, 10) : 1080;
    if (!isFinite(h) || h < 120) h = 1080;
    P.H = even(h);
    P.W = even(h * ar);
    layoutStage();
    renderAll();
}

function even(v) { return Math.max(2, Math.round(v / 2) * 2); }

function wireExportControls() {
    var btn = $('ceExportBtn');
    if (btn) { els.exportBtn = btn; btn.addEventListener('click', doExport); }
    var cancel = $('ceCancelBtn');
    if (cancel) cancel.addEventListener('click', function () { exportAbort && exportAbort.abort(); });
    els.progress = $('ceProgress');
    els.progressFill = $('ceProgressFill');
    els.progressLabel = $('ceProgressLabel');
    var hSel = $('ceHeight');
    if (hSel) hSel.addEventListener('change', function () { applyOutputSize(); savePref('height', hSel.value); });
    var fSel = $('ceFormat');
    if (fSel) fSel.addEventListener('change', function () { savePref('format', fSel.value); });
}

async function doExport() {
    if (P.clips.length < 2) { toast(t('need_two'), 'warning'); return; }

    // One COMPOSITION is the unit, not one file: a clone shot needs at least
    // two takes, so a per-file unit would bill two for the tool's minimum job
    // and a low cap would refuse it outright. Deriving the key from the files
    // also makes re-exporting the same takes after moving the seam free for the
    // rest of the day, which is how this tool is actually used.
    var setKey = null;
    if (window.__toolLimit) {
        var files = P.clips.map(function (c) { return c.file; });
        if (P.music) files.push(P.music.file);
        setKey = 'set:' + window.__toolLimit.fileKeys(files).sort().join('~');
    }
    var run = window.__toolLimit ? window.__toolLimit.begin(SLUG, setKey) : null;
    if (window.__toolLimit && !run) return;   // wall shown by the shared block

    stopPlayback();
    exportAbort = new AbortController();
    els.exportBtn.disabled = true;
    els.progress.hidden = false;
    setProgress(0, t('stage_audio'));
    stage('exporting');

    var fmtEl = $('ceFormat');
    var format = fmtEl && fmtEl.value === 'webm' ? 'webm' : 'mp4';
    var t0 = performance.now();

    // Stall watchdog. An export that stops advancing looks exactly like a slow
    // one, and the difference decides whether the user waits or leaves — so it
    // is reported rather than left to the progress bar to imply.
    var lastStage = 'audio', lastProgress = 0, lastMove = performance.now(), stallSent = false;
    var watchdog = setInterval(function () {
        if (performance.now() - lastMove < 45000 || stallSent) return;
        stallSent = true;
        stage('stalled', { at: lastStage, pct: Math.round(lastProgress * 100) });
        ae('feature_use', { f: 'export_stalled', at: lastStage, pct: Math.round(lastProgress * 100) });
    }, 5000);

    try {
        var res = await window.CloneExport.run(P, {
            format: format, quality: P.quality, fps: P.fps,
            W: window.CloneReframe.outputSize(P).w, H: window.CloneReframe.outputSize(P).h
        }, {
            signal: exportAbort.signal,
            onProgress: function (f) {
                if (f > lastProgress + 0.001) { lastProgress = f; lastMove = performance.now(); }
                setProgress(f);
            },
            onStage: function (k) { lastStage = k; lastMove = performance.now(); setProgress(null, t('stage_' + k) || ''); },
            onNote: function (kind) {
                setProgress(null, kind === 'plate' ? t('busy_plate') : t('busy_model'));
            }
        });

        run && run.commit();
        showResult(res, format);
        stage('done');
        var secs = (performance.now() - t0) / 1000;
        ae('feature_use', { f: 'export_ok', lane: res.lane, mode: res.mode, took: Math.round(secs), dur: Math.round(res.duration) });
        try {
            window.__aeToolComplete && window.__aeToolComplete({
                slug: SLUG, ok: 1, lane: res.lane, duration: Math.round(res.duration), took: Math.round(secs)
            });
        } catch (_) {}
    } catch (e) {
        run && run.abort();
        if (e && e.name === 'AbortError') {
            toast(t('cancelled'), 'info');
            ae('feature_use', { f: 'export_cancelled' });
        } else {
            try { window.__handleError && window.__handleError(e, SLUG, { say: t('err_export') }); } catch (_) {}
            var caps = probeCaps();
            // tool_error is the type the "real bugs" dashboard reads; feature_use
            // and __aeCritical alone leave a failed export invisible there.
            try {
                window.__ae && window.__ae('tool_error', {
                    slug: SLUG, err: ((e && e.message) || '').slice(0, 160),
                    stage: lastStage, lane: caps.lane, n: P.clips.length,
                    w: P.W, h: P.H, dur: Math.round(window.CloneScene.projectDuration(P))
                });
            } catch (_) {}
            ae('feature_use', { f: 'export_failed', err: ((e && e.message) || '').slice(0, 80), lane: caps.lane });
            try { window.__aeCritical && window.__aeCritical('export_failed', { slug: SLUG, err: ((e && e.message) || '').slice(0, 80) }); } catch (_) {}
        }
    } finally {
        clearInterval(watchdog);
        exportAbort = null;
        els.exportBtn.disabled = false;
        els.progress.hidden = true;
    }
}

function setProgress(frac, label) {
    if (!els.progressFill) return;
    if (frac != null) els.progressFill.style.width = Math.round(Math.max(0, Math.min(1, frac)) * 100) + '%';
    if (label) els.progressLabel.textContent = label;
}

function showResult(res, format) {
    var host = els.result;
    host.innerHTML = '';
    host.hidden = false;

    var v = el('video', 'ce-result-video');
    v.controls = true; v.playsInline = true;
    v.src = URL.createObjectURL(res.blob);
    host.appendChild(v);

    var meta = el('div', 'ce-result-meta',
        fmtTime(res.duration) + ' · ' + P.W + '×' + P.H + ' · ' + (res.blob.size / 1048576).toFixed(1) + ' MB');
    host.appendChild(meta);

    var name = 'clone-' + Date.now() + '.' + (format === 'webm' ? 'webm' : 'mp4');
    var dl = button('⬇ ' + t('download'), 'primary big', function () {
        window.__downloadFile(res.blob, name);
        ae('feature_use', { f: 'download', fmt: format });
    });
    host.appendChild(dl);
    host.scrollIntoView({ block: 'nearest' });
}

/* ─────────────────────────── small controls ─────────────────────────── */

function toggle(value, onchange) {
    var b = el('button', 'ce-toggle' + (value ? ' on' : ''));
    b.type = 'button';
    b.setAttribute('role', 'switch');
    b.setAttribute('aria-checked', value ? 'true' : 'false');
    b.innerHTML = '<span class="ce-toggle-knob"></span>';
    b.addEventListener('click', function () {
        value = !value;
        b.classList.toggle('on', value);
        b.setAttribute('aria-checked', value ? 'true' : 'false');
        onchange(value);
    });
    return b;
}

function wrapVal(control, valEl) {
    var w = el('div', 'ce-slide-wrap');
    w.appendChild(control);
    w.appendChild(valEl);
    return w;
}

function actionRow(btn, hint) {
    var w = el('div', 'ce-action');
    w.appendChild(btn);
    if (hint) w.appendChild(el('div', 'ce-hint', hint));
    return w;
}

function setBusy(on, label) {
    var b = $('ceBusy');
    if (!b) return;
    b.hidden = !on;
    if (on && label) {
        var l = b.querySelector('.ce-busy-label');
        if (l) l.textContent = label;
    }
}

window.CloneUI = {
    init: function (translations) {
        init(translations);
        wireMusicUploader();
    },
    _state: function () { return { P: P, sel: sel, playhead: playhead }; },
    /* Test surface: the REAL functions, so a green run proves the shipped path
       rather than a parallel copy of it. */
    _trimDeadAir: function () { return trimDeadAir(); },
    _arrangeTurns: function () { return arrangeTurns(); },
    _audio: function () { return { ctx: actx, node: mixNode, playing: playing }; },
    _setFrameMode: function (m) { return setFrameMode(m); },
    _setGap: function (v) { turnGap = v; }
};
})();
